

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance.js';
import TaskService from './TaskService';
import CenteredDialog from './CenteredDialog';
import {
  employeeHasAccessToApplication,
  getApplicationMismatchMessages,
  getInvalidEmployeeFlagsForApplication,
} from '../utils/employeeApplicationAccess.js';
import {
  computeDueDateFromSlaWithLookups,
  toDateTimeLocalInputValue,
} from '../utils/taskSlaDueDate.js';
import { refreshNotificationCount } from '../utils/notificationPolling';
import { formatTaskSuccessMessage } from '../utils/formatTaskSuccessMessage';
import './UpdateTaskScreen.css';

import API_BASE_URL from './common/common.js';

const APPLICATION_DETAILS_STORAGE_KEY = "application_details";

function persistApplicationDetailsToStorage(applicationDetails) {
  if (!Array.isArray(applicationDetails)) return;
  localStorage.setItem(
    APPLICATION_DETAILS_STORAGE_KEY,
    JSON.stringify(applicationDetails)
  );
}

function resolveApplicationFunctionalLeadId(applicationId, applicationDetails) {
  const normalizedAppId = Number(applicationId);
  if (!Number.isFinite(normalizedAppId)) {
    return null;
  }
  let availableApplications = Array.isArray(applicationDetails)
    ? applicationDetails
    : [];
  if (!availableApplications.length) {
    try {
      availableApplications = JSON.parse(
        localStorage.getItem(APPLICATION_DETAILS_STORAGE_KEY) || "[]"
      );
    } catch (error) {
      availableApplications = [];
    }
  }
  const selectedApplication = availableApplications.find(
    (app) => Number(app?.aa_app_rl_application_id) === normalizedAppId
  );
  if (!selectedApplication) return null;
  const functionalLeadId = selectedApplication.aa_app_rl_application_functional_lead_id;
  return functionalLeadId == null ? null : Number(functionalLeadId);
}

const UpdateTaskScreen = ({ userData = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams();
  const currentUserId = localStorage.getItem('employee_uid');
  console.log(`The employee_uid value for update task screen page is ${currentUserId}`);
  
  // State for the task
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTaskOwner, setIsTaskOwner] = useState(false);
  /** True when current user is the task creator (aa_app_task_created_by); may edit due date + task owner on status form. */
  const [isTaskCreator, setIsTaskCreator] = useState(false);
  /** True when current user's employee UID matches the application's functional lead (lookup). */
  const [isFunctionalLead, setIsFunctionalLead] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, title: "", message: "", type: "success" });
  
  // State for file attachments (available to everyone)
  const [attachedFiles, setAttachedFiles] = useState([]);
  const attachedFilesRef = useRef([]);
  // State for completion-specific file (single file when status is completed)
  const [completionFile, setCompletionFile] = useState(null);
  const completionFileRef = useRef(null);
  const [completionFilePreviewUrl, setCompletionFilePreviewUrl] = useState(null);
  const [imageViewer, setImageViewer] = useState({ isOpen: false, imageUrl: null, fileName: null });
  const [isSecondaryOwner, setIsSecondaryOwner] = useState(false);
  const [isAssignee, setIsAssignee] = useState(false);

  
  // State for API data
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]); // Added task types state
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [applications, setApplications] = useState([]);
  const [completionTypes, setCompletionTypes] = useState([]); // Completion types from backend
  const [releaseTypes, setReleaseTypes] = useState([]); // From fetchAllLookupDetails.release_type_details
  const [taskSlaDetails, setTaskSlaDetails] = useState([]);

  /**
   * After loading a task from the API, SLA auto-calc must not replace `dueDate` until the user
   * changes application, priority, or start (driving fields). Values set when task details load.
   */
  const ownerSlaHydrationSnapshotRef = useRef(null);
  const statusSlaHydrationSnapshotRef = useRef(null);
  
  // State for status history
  const [statusHistory, setStatusHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Multi-select state for both forms
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [statusFilteredEmployees, setStatusFilteredEmployees] = useState([]);
  const [taskFilteredEmployees, setTaskFilteredEmployees] = useState([]);
  const [statusSelectedEmployees, setStatusSelectedEmployees] = useState([]);
  const [taskSelectedEmployees, setTaskSelectedEmployees] = useState([]);

  // Task owner state for both forms (status update and owner view)
  const [isStatusOwnerDropdownOpen, setIsStatusOwnerDropdownOpen] = useState(false);
  const [isTaskOwnerDropdownOpen, setIsTaskOwnerDropdownOpen] = useState(false);
  const [statusSelectedOwner, setStatusSelectedOwner] = useState(null);
  const [taskSelectedOwner, setTaskSelectedOwner] = useState(null);
  const [statusOwnerSearchTerm, setStatusOwnerSearchTerm] = useState("");
  const [taskOwnerSearchTerm, setTaskOwnerSearchTerm] = useState("");
  const [statusFilteredOwners, setStatusFilteredOwners] = useState([]);
  const [taskFilteredOwners, setTaskFilteredOwners] = useState([]);

  // Secondary owners state for both forms (status update and owner view)
  const [isStatusSecondaryOwnerDropdownOpen, setIsStatusSecondaryOwnerDropdownOpen] = useState(false);
  const [isTaskSecondaryOwnerDropdownOpen, setIsTaskSecondaryOwnerDropdownOpen] = useState(false);
  const [statusSelectedSecondaryOwners, setStatusSelectedSecondaryOwners] = useState([]);
  const [taskSelectedSecondaryOwners, setTaskSelectedSecondaryOwners] = useState([]);
  const [statusSecondaryOwnerSearchTerm, setStatusSecondaryOwnerSearchTerm] = useState("");
  const [taskSecondaryOwnerSearchTerm, setTaskSecondaryOwnerSearchTerm] = useState("");
  const [statusFilteredSecondaryOwners, setStatusFilteredSecondaryOwners] = useState([]);
  const [taskFilteredSecondaryOwners, setTaskFilteredSecondaryOwners] = useState([]);
  
  // Form data for status updates (for non-owner) - UPDATED to include task owner, task type, and secondary owners
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    completion: 0,
    comment: '',
    issues: '',
    assignedTo: [], // Changed to array
    priority: '',
    dueDate: '', // SLA-derived; updates when priority changes (non-owner status view)
    taskOwner: '', // Added task owner
    taskType: '', // Added task type
    secondaryOwners: [], // Added secondary owners
    title: '', // Editable by functional lead (status form) when not task owner
    description: '',
    compTypeId: '', // Completion type ID when status is completed
    compStatusTextDesc: '', // Completion text description when status is completed
    releaseType: '', // aa_app_rl_release_type_id (string for select)
    releaseDate: '', // aa_app_rl_release_date (YYYY-MM-DD)
  });
  
  // Form data for task details (for owner) - UPDATED to include task owner, task type, and secondary owners
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    assignedTo: [], // Changed to array
    application: '',
    startDate: '',
    dueDate: '',
    priority: '',
    status: '',
    completion: 0,
    comment: '',
    issues: '',
    taskOwner: '', // Added task owner
    taskType: '', // Added task type
    secondaryOwners: [], // Added secondary owners
    compTypeId: '', // Completion type ID when status is completed
    compStatusTextDesc: '', // Completion text description when status is completed
    releaseType: '', // aa_app_rl_release_type_id (string for select)
    releaseDate: '' // aa_app_rl_release_date (YYYY-MM-DD)
  });

  // Helper function to check if status is "completed"
  const isStatusCompleted = (statusId) => {
    if (!statusId || !statuses.length) return false;
    const status = statuses.find(s => s.aa_app_rl_task_status_id === parseInt(statusId));
    if (!status) return false;
    // Check if status name contains "completed" (case-insensitive)
    return status.aa_app_rl_task_status_name.toLowerCase().includes('completed');
  };

  /** Completed / Cancelled: due date may be empty on save (other statuses still require a due date). */
  const isStatusCompletedOrCancelled = (statusId) => {
    if (!statusId || !statuses.length) return false;
    const status = statuses.find((s) => s.aa_app_rl_task_status_id === parseInt(statusId, 10));
    if (!status) return false;
    const n = status.aa_app_rl_task_status_name.toLowerCase();
    return n.includes("completed") || n.includes("cancelled");
  };

  // Helper function to parse assigned users from string to array
  const parseAssignedUsers = (assignedToValue) => {
    if (!assignedToValue) return [];
    
    // If it's already an array, return it
    if (Array.isArray(assignedToValue)) {
      return assignedToValue.map(id => String(id).trim());
    }
    
    // If it's a string, split by comma and clean up
    if (typeof assignedToValue === 'string') {
      return assignedToValue
        .split(',')
        .map(id => String(id).trim())
        .filter(id => id !== ''); // Remove empty strings
    }
    
    // If it's a single number, convert to array
    return [String(assignedToValue)];
  };

  const statusApplicationEmployeeMismatch = useMemo(() => {
    const appId =
      task?.applicationId != null && task.applicationId !== ""
        ? String(task.applicationId)
        : "";
    return {
      messages: getApplicationMismatchMessages(
        appId,
        statusSelectedOwner,
        statusSelectedSecondaryOwners,
        statusSelectedEmployees
      ),
      ...getInvalidEmployeeFlagsForApplication(
        appId,
        statusSelectedOwner,
        statusSelectedSecondaryOwners,
        statusSelectedEmployees
      ),
    };
  }, [
    task?.applicationId,
    statusSelectedOwner,
    statusSelectedSecondaryOwners,
    statusSelectedEmployees,
  ]);

  const taskApplicationEmployeeMismatch = useMemo(
    () => ({
      messages: getApplicationMismatchMessages(
        taskFormData.application,
        taskSelectedOwner,
        taskSelectedSecondaryOwners,
        taskSelectedEmployees
      ),
      ...getInvalidEmployeeFlagsForApplication(
        taskFormData.application,
        taskSelectedOwner,
        taskSelectedSecondaryOwners,
        taskSelectedEmployees
      ),
    }),
    [
      taskFormData.application,
      taskSelectedOwner,
      taskSelectedSecondaryOwners,
      taskSelectedEmployees,
    ]
  );

  // Filter employees: task application + search (status form)
  useEffect(() => {
    if (employees.length > 0) {
      const appId =
        task?.applicationId != null && task.applicationId !== ""
          ? String(task.applicationId)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = statusSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setStatusFilteredEmployees(filtered);
    }
  }, [employees, statusSearchTerm, task?.applicationId]);

  // Filter employees: selected application + search (task form / owner view)
  useEffect(() => {
    if (employees.length > 0) {
      const appId =
        taskFormData.application != null && taskFormData.application !== ""
          ? String(taskFormData.application)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = taskSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setTaskFilteredEmployees(filtered);
    }
  }, [employees, taskSearchTerm, taskFormData.application]);

  // Filter owners for status form
  useEffect(() => {
    if (employees.length > 0) {
      const appId =
        task?.applicationId != null && task.applicationId !== ""
          ? String(task.applicationId)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = statusOwnerSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setStatusFilteredOwners(filtered);
    }
  }, [employees, statusOwnerSearchTerm, task?.applicationId]);

  // Filter owners for task form
  useEffect(() => {
    if (employees.length > 0) {
      const appId =
        taskFormData.application != null && taskFormData.application !== ""
          ? String(taskFormData.application)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
          const n = (employee.name || "").toLowerCase();
          const d = (employee.department || "").toLowerCase();
          const em = (employee.email || "").toLowerCase();
          const q = taskOwnerSearchTerm.toLowerCase();
          return n.includes(q) || d.includes(q) || em.includes(q);
        });
      setTaskFilteredOwners(filtered);
    }
  }, [employees, taskOwnerSearchTerm, taskFormData.application]);

  // Filter secondary owners for status form - exclude task owner
  useEffect(() => {
    if (employees.length > 0) {
      const taskOwnerId = statusFormData.taskOwner ? String(statusFormData.taskOwner).trim() : '';
      const appId =
        task?.applicationId != null && task.applicationId !== ""
          ? String(task.applicationId)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
        const employeeId = String(employee.id || employee.uid || '').trim();
        if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
          return false;
        }
        const n = (employee.name || "").toLowerCase();
        const d = (employee.department || "").toLowerCase();
        const em = (employee.email || "").toLowerCase();
        const q = statusSecondaryOwnerSearchTerm.toLowerCase();
        return n.includes(q) || d.includes(q) || em.includes(q);
      });
      setStatusFilteredSecondaryOwners(filtered);
    }
  }, [employees, statusSecondaryOwnerSearchTerm, statusFormData.taskOwner, task?.applicationId]);

  // Filter secondary owners for task form - exclude task owner
  useEffect(() => {
    if (employees.length > 0) {
      const taskOwnerId = taskFormData.taskOwner ? String(taskFormData.taskOwner).trim() : '';
      const appId =
        taskFormData.application != null && taskFormData.application !== ""
          ? String(taskFormData.application)
          : "";
      const filtered = employees
        .filter((employee) => employeeHasAccessToApplication(employee, appId))
        .filter((employee) => {
        const employeeId = String(employee.id || employee.uid || '').trim();
        if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
          return false;
        }
        const n = (employee.name || "").toLowerCase();
        const d = (employee.department || "").toLowerCase();
        const em = (employee.email || "").toLowerCase();
        const q = taskSecondaryOwnerSearchTerm.toLowerCase();
        return n.includes(q) || d.includes(q) || em.includes(q);
      });
      setTaskFilteredSecondaryOwners(filtered);
    }
  }, [employees, taskSecondaryOwnerSearchTerm, taskFormData.taskOwner, taskFormData.application]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const statusDropdown = event.target.closest('.status-multi-select-dropdown');
      const taskDropdown = event.target.closest('.task-multi-select-dropdown');
      const statusOwnerDropdown = event.target.closest('.status-owner-dropdown-container');
      const taskOwnerDropdown = event.target.closest('.task-owner-dropdown-container');
      const statusSecondaryOwnerDropdown = event.target.closest('.status-secondary-owner-dropdown');
      const taskSecondaryOwnerDropdown = event.target.closest('.task-secondary-owner-dropdown');
      
      if (!statusDropdown && isStatusDropdownOpen) {
        setIsStatusDropdownOpen(false);
      }
      if (!taskDropdown && isTaskDropdownOpen) {
        setIsTaskDropdownOpen(false);
      }
      if (!statusOwnerDropdown && isStatusOwnerDropdownOpen) {
        setIsStatusOwnerDropdownOpen(false);
      }
      if (!taskOwnerDropdown && isTaskOwnerDropdownOpen) {
        setIsTaskOwnerDropdownOpen(false);
      }
      if (!statusSecondaryOwnerDropdown && isStatusSecondaryOwnerDropdownOpen) {
        setIsStatusSecondaryOwnerDropdownOpen(false);
      }
      if (!taskSecondaryOwnerDropdown && isTaskSecondaryOwnerDropdownOpen) {
        setIsTaskSecondaryOwnerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStatusDropdownOpen, isTaskDropdownOpen, isStatusOwnerDropdownOpen, isTaskOwnerDropdownOpen, isStatusSecondaryOwnerDropdownOpen, isTaskSecondaryOwnerDropdownOpen]);

  // Add keyboard support for accessibility
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isStatusDropdownOpen) {
          setIsStatusDropdownOpen(false);
          setStatusSearchTerm("");
        }
        if (isTaskDropdownOpen) {
          setIsTaskDropdownOpen(false);
          setTaskSearchTerm("");
        }
        if (isStatusOwnerDropdownOpen) {
          setIsStatusOwnerDropdownOpen(false);
          setStatusOwnerSearchTerm("");
        }
        if (isTaskOwnerDropdownOpen) {
          setIsTaskOwnerDropdownOpen(false);
          setTaskOwnerSearchTerm("");
        }
        if (isStatusSecondaryOwnerDropdownOpen) {
          setIsStatusSecondaryOwnerDropdownOpen(false);
          setStatusSecondaryOwnerSearchTerm("");
        }
        if (isTaskSecondaryOwnerDropdownOpen) {
          setIsTaskSecondaryOwnerDropdownOpen(false);
          setTaskSecondaryOwnerSearchTerm("");
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isStatusDropdownOpen, isTaskDropdownOpen, isStatusOwnerDropdownOpen, isTaskOwnerDropdownOpen, isStatusSecondaryOwnerDropdownOpen, isTaskSecondaryOwnerDropdownOpen]);
  
  // Fetch task data and options
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        setIsTaskOwner(false);
        setIsTaskCreator(false);
        ownerSlaHydrationSnapshotRef.current = { loading: true };
        statusSlaHydrationSnapshotRef.current = { loading: true };

        // Fetch all lookup details (priorities, task types, statuses, employees, applications) in one call - force refresh to always get latest data
        console.log("🚀 Fetching all lookup details...");
        const lookupData = await TaskService.fetchAllLookupDetails(true);
        console.log("✅ All lookup details received:", lookupData);
        
        // Set priorities
        if (lookupData.task_priority_details) {
          setPriorities(lookupData.task_priority_details);
        }
        
        // Set task types
        if (lookupData.task_type_details) {
          setTaskTypes(lookupData.task_type_details);
        }
        
        // Sort statuses alphabetically and set them
        if (lookupData.task_status_details) {
          const sortedStatuses = lookupData.task_status_details.sort((a, b) => 
            a.aa_app_rl_task_status_name.localeCompare(b.aa_app_rl_task_status_name)
          );
          setStatuses(sortedStatuses);
        }
        
        // Transform and sort the employees data
        let sortedEmployees = [];
        if (lookupData.employee_details) {
          const formattedEmployees = lookupData.employee_details.map(emp => ({
            id: emp.aa_app_employee_uid,
            uid: emp.aa_app_employee_uid,
            employeeId: emp.aa_app_employee_id,
            name: emp.aa_app_employee_name,
            email: emp.aa_app_employee_email,
            department: emp.aa_app_resource_deptname,
            defaultApplicationIds: emp.aa_app_rl_default_application_id,
            roleId: emp.aa_app_rl_role_id,
          }));
          
          // Sort employees by name in ascending order
          sortedEmployees = formattedEmployees.sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          
          setEmployees(sortedEmployees);
          setStatusFilteredEmployees(sortedEmployees);
          setTaskFilteredEmployees(sortedEmployees);
          setStatusFilteredOwners(sortedEmployees);
          setTaskFilteredOwners(sortedEmployees);
        }
        
        // Set applications
        if (lookupData.application_details) {
          setApplications(lookupData.application_details);
          persistApplicationDetailsToStorage(lookupData.application_details);
        }
        
        // Set completion types from backend
        if (lookupData.completion_type_details) {
          setCompletionTypes(lookupData.completion_type_details);
          console.log("✅ Completion types loaded from backend:", lookupData.completion_type_details);
        }

        if (lookupData.release_type_details) {
          setReleaseTypes(
            [...lookupData.release_type_details].sort(
              (a, b) => a.aa_app_rl_release_type_id - b.aa_app_rl_release_type_id
            )
          );
        }

        if (Array.isArray(lookupData.task_sla_details)) {
          setTaskSlaDetails(lookupData.task_sla_details);
        } else {
          setTaskSlaDetails([]);
        }
        
        // Fetch task details using the new endpoint
        const taskDetails = await TaskService.getTaskById(taskId);
        
        // Check if current user is the task owner (not just the creator)
        // Task owner is stored in aa_app_task_owner_id, which can be different from aa_app_task_created_by
        const taskOwnerId = taskDetails.aa_app_task_owner_id || taskDetails.aa_app_task_created_by || '';
        console.log(`Checking task ownership - currentUserId: ${currentUserId}, taskOwnerId: ${taskOwnerId}, createdBy: ${taskDetails.aa_app_task_created_by}`);
        // Use strict comparison and ensure both values are strings for consistent comparison
        const ownerId = String(taskOwnerId || '').trim();
        const userId = String(currentUserId || '').trim();
        const isOwner = ownerId !== '' && userId !== '' && ownerId === userId;
        console.log(`isTaskOwner value is ${isOwner} (taskOwnerId: "${ownerId}", userId: "${userId}")`);
        setIsTaskOwner(isOwner);

        const createdById = String(taskDetails.aa_app_task_created_by ?? "").trim();
        const isCreator =
          userId !== "" && createdById !== "" && userId === createdById;
        setIsTaskCreator(isCreator);

        const applicationRows = lookupData.application_details || [];
        const functionalLeadUid = resolveApplicationFunctionalLeadId(
          taskDetails.aa_app_rl_application_id,
          applicationRows
        );
        const functionalLeadStr =
          functionalLeadUid != null && Number.isFinite(Number(functionalLeadUid))
            ? String(Number(functionalLeadUid)).trim()
            : "";
        const isLead =
          functionalLeadStr !== "" &&
          userId !== "" &&
          functionalLeadStr === userId;
        console.log(
          `isFunctionalLead: ${isLead} (functionalLeadUid: "${functionalLeadStr}", userId: "${userId}")`
        );
        setIsFunctionalLead(isLead);
        
        // Handle assigned employees
        let assignedEmployeeIds = [];
        let assignedEmployeesData = [];
        
        if (taskDetails.aa_app_task_assigned_to) {
          // Parse the assigned users using the helper function
          assignedEmployeeIds = parseAssignedUsers(taskDetails.aa_app_task_assigned_to);
          
          console.log('Parsed assigned employee IDs:', assignedEmployeeIds);
          
          // Find employee objects for the assigned IDs
          assignedEmployeesData = sortedEmployees.filter(emp => {
            const isAssigned = assignedEmployeeIds.includes(String(emp.id));
            if (isAssigned) {
              console.log(`Found assigned employee: ${emp.name} (ID: ${emp.id})`);
            }
            return isAssigned;
          });
          
          console.log('Found assigned employees:', assignedEmployeesData);
        }

        // Handle task owner
        let taskOwnerData = null;
        if (taskDetails.aa_app_task_owner_id) {
          taskOwnerData = sortedEmployees.find(emp => 
            String(emp.id) === String(taskDetails.aa_app_task_owner_id)
          );
          if (taskOwnerData) {
            console.log('Found task owner:', taskOwnerData);
          }
        }

        // Handle secondary owners
        let secondaryOwnerIds = [];
        let secondaryOwnersData = [];
        
        // Check multiple possible field names for secondary owners
        const secondaryOwnersField = taskDetails.aa_app_task_secondary_owners || 
                                     taskDetails.aa_app_task_secondary_owner_id ||
                                     taskDetails.secondary_owners ||
                                     taskDetails.secondaryOwners;
        
        console.log('Task details for secondary owners:', {
          'aa_app_task_secondary_owners': taskDetails.aa_app_task_secondary_owners,
          'aa_app_task_secondary_owner_id': taskDetails.aa_app_task_secondary_owner_id,
          'secondary_owners': taskDetails.secondary_owners,
          'secondaryOwners': taskDetails.secondaryOwners,
          'allKeys': Object.keys(taskDetails).filter(key => key.toLowerCase().includes('secondary'))
        });
        
        if (secondaryOwnersField) {
          // Parse secondary owners (could be array or string)
          secondaryOwnerIds = parseAssignedUsers(secondaryOwnersField);
          
          console.log('Parsed secondary owner IDs:', secondaryOwnerIds);
          console.log('Available employees (first 3):', sortedEmployees.slice(0, 3).map(e => ({ id: e.id, uid: e.uid, employeeId: e.employeeId, name: e.name })));
          
          // Find employee objects for the secondary owner IDs
          // Check both uid and employeeId to handle different ID formats
          secondaryOwnersData = sortedEmployees.filter(emp => {
            const matchesUid = secondaryOwnerIds.includes(String(emp.id)) || secondaryOwnerIds.includes(String(emp.uid));
            const matchesEmployeeId = secondaryOwnerIds.includes(String(emp.employeeId));
            const isSecondaryOwner = matchesUid || matchesEmployeeId;
            
            if (isSecondaryOwner) {
              console.log(`Found secondary owner: ${emp.name} (UID: ${emp.uid}, EmployeeID: ${emp.employeeId}, Matched: ${matchesUid ? 'UID' : 'EmployeeID'})`);
            }
            return isSecondaryOwner;
          });
          
          console.log('Found secondary owners:', secondaryOwnersData);
          if (secondaryOwnerIds.length > 0 && secondaryOwnersData.length === 0) {
            console.warn('⚠️ Secondary owner IDs found but no matching employees!', {
              'Looking for IDs': secondaryOwnerIds,
              'Employee UIDs': sortedEmployees.map(e => e.uid).slice(0, 5),
              'Employee IDs': sortedEmployees.map(e => e.employeeId).slice(0, 5)
            });
          }
        } else {
          console.log('No secondary owners field found in task details');
          console.log('All task detail keys:', Object.keys(taskDetails).sort());
        }

        const creatorUid =
          taskDetails.aa_app_task_created_by ??
          taskDetails.aa_app_task_created_by_id ??
          '';
        const apiCreatedByNames = [
          taskDetails.aa_app_task_created_by_name,
          taskDetails.aa_app_rl_created_by,
          taskDetails.created_by_name,
        ];
        let resolvedCreatedByName = '';
        for (const cand of apiCreatedByNames) {
          if (cand == null || cand === '') continue;
          const s = String(cand).trim();
          if (!s) continue;
          const looksLikeIdOnly =
            /^\d+$/.test(s) || s === String(creatorUid).trim();
          if (!looksLikeIdOnly) {
            resolvedCreatedByName = s;
            break;
          }
        }
        if (
          !resolvedCreatedByName &&
          sortedEmployees.length > 0 &&
          creatorUid !== '' &&
          creatorUid != null
        ) {
          const idStr = String(creatorUid).trim();
          const creatorEmp = sortedEmployees.find(
            (e) =>
              String(e.id) === idStr ||
              String(e.uid) === idStr ||
              String(e.employeeId) === idStr
          );
          if (creatorEmp?.name) {
            resolvedCreatedByName = creatorEmp.name;
          }
        }
        if (!resolvedCreatedByName) {
          const fallbackFromApi = apiCreatedByNames.find(
            (c) => c != null && String(c).trim() !== ''
          );
          resolvedCreatedByName =
            (fallbackFromApi != null ? String(fallbackFromApi).trim() : '') ||
            String(creatorUid || '').trim() ||
            '—';
        }

        // Format the task data for our component
        const formattedTask = {
          id: taskDetails.aa_app_rl_resource_task_uid,
          title: taskDetails.aa_app_rl_resource_task_title,
          description: taskDetails.aa_app_rl_resource_task_desc,
          assignedTo: assignedEmployeeIds, // Array of employee IDs
          assignedToName: taskDetails.aa_app_rl_assigned_to_name,
          createdBy: taskDetails.aa_app_task_created_by,
          createdByName: resolvedCreatedByName,
          taskOwner: taskDetails.aa_app_task_owner_id || '',
          taskOwnerName: taskDetails.aa_app_task_owner_name || '',
          secondaryOwners: secondaryOwnerIds, // Array of secondary owner IDs
          status: taskDetails.aa_app_rl_task_status_id,
          statusName: taskDetails.aa_app_rl_task_status_name,
          completion: taskDetails.aa_app_rl_task_completion_percentage || 0,
          dueDate: toDateTimeLocalInputValue(taskDetails.aa_app_task_due_date),
          application: taskDetails.aa_app_rl_application_name,
          applicationId: taskDetails.aa_app_rl_application_id,
          priority: taskDetails.aa_app_rl_resource_task_priority_id,
          priorityName: taskDetails.aa_app_rl_priority_name,
          taskType: taskDetails.aa_app_rl_task_type_id || '', // Added task type
          taskTypeName: taskDetails.aa_app_rl_task_type_name || '', // Added task type name
          startDate: toDateTimeLocalInputValue(
            taskDetails.aa_app_task_start_date ?? taskDetails.aa_app_task_creation_date
          ),
          creationDate: formatDateForInput(taskDetails.aa_app_task_creation_date),
          issues: taskDetails.aa_app_rl_task_status_issue_desc || '',
          statusUpdateDesc: taskDetails.aa_app_rl_task_status_update_desc || '',
          attachments: taskDetails.attachments || [], // Add attachments from API
          // Completion fields
          compTypeId: taskDetails.aa_app_rl_comp_type_id || 0,
          compTypeName: taskDetails.aa_app_rl_comp_type_name || '',
          compStatusTextDesc: taskDetails.aa_app_rl_comp_status_text_desc || '',
          compStatusAttachFileName: taskDetails.aa_app_rl_comp_status_attach_file_name || '',
          compStatusAttachFileType: taskDetails.aa_app_rl_comp_status_attach_file_type || '',
          compStatusFileDownloadUrl: taskDetails.aa_app_task_completion_file_download_url || '',
          compStatusFileViewUrl: taskDetails.aa_app_task_completion_file_view_url || '',
          compStatusFileObjectKey: taskDetails.aa_app_task_completion_file_object_key || '',
          releaseTypeName: taskDetails.aa_app_rl_release_type_name || '',
          releaseType: (() => {
            const raw =
              taskDetails.aa_app_rl_release_type_id ?? taskDetails.mock_release_type;
            if (raw === null || raw === undefined || raw === '') return '';
            const s = String(raw).trim();
            if (s === '' || s.toUpperCase() === 'NULL') return '';
            const n = parseInt(s, 10);
            return Number.isNaN(n) ? '' : String(n);
          })(),
          releaseDate: (() => {
            const raw =
              taskDetails.aa_app_rl_release_date ?? taskDetails.mock_release_date;
            if (raw === null || raw === undefined || raw === '') return '';
            const s = String(raw).trim();
            if (s === '' || s.toUpperCase() === 'NULL') return '';
            return formatDateForInput(s);
          })()
        };

        ownerSlaHydrationSnapshotRef.current = {
          applicationId: formattedTask.applicationId,
          priorityId: formattedTask.priority,
          startDate: formattedTask.startDate,
        };
        statusSlaHydrationSnapshotRef.current = {
          applicationId: formattedTask.applicationId,
          priorityId: formattedTask.priority,
          startDate: formattedTask.startDate,
        };
        
        console.log('📎 Task details received:', taskDetails);
        console.log('📎 Formatted task with attachments:', formattedTask);
        console.log('📎 Number of attachments:', formattedTask.attachments ? formattedTask.attachments.length : 0);
        console.log('📎 Attachments data:', formattedTask.attachments);
        console.log('📎 File attachments from backend:', taskDetails.file_attachments);
        
        setTask(formattedTask);

        // Set selected employees, owners, and secondary owners for both forms
        setStatusSelectedEmployees(assignedEmployeesData);
        setTaskSelectedEmployees(assignedEmployeesData);
        setStatusSelectedSecondaryOwners(secondaryOwnersData);
        setTaskSelectedSecondaryOwners(secondaryOwnersData);
        
        if (taskOwnerData) {
          setStatusSelectedOwner(taskOwnerData);
          setTaskSelectedOwner(taskOwnerData);
          setStatusOwnerSearchTerm(taskOwnerData.name);
          setTaskOwnerSearchTerm(taskOwnerData.name);
        }
        
        console.log('Set status selected employees:', assignedEmployeesData);
        console.log('Set task selected employees:', assignedEmployeesData);
        console.log('Set task owner:', taskOwnerData);
        console.log('Set secondary owners:', secondaryOwnersData);
        
        // Check if current user is a secondary owner
        const checkIsSecondaryOwner = () => {
          if (!currentUserId || secondaryOwnerIds.length === 0) {
            console.log('Secondary owner check: No currentUserId or no secondary owners');
            return false;
          }
          const userId = String(currentUserId).trim();
          const isSecondary = secondaryOwnerIds.some(id => {
            const secondaryId = String(id).trim();
            return secondaryId !== '' && userId !== '' && secondaryId === userId;
          });
          console.log('Secondary owner check:', isSecondary, 'userId:', userId, 'secondaryOwnerIds:', secondaryOwnerIds);
          return isSecondary;
        };
        const userIsSecondaryOwner = checkIsSecondaryOwner();
        setIsSecondaryOwner(userIsSecondaryOwner);
        console.log('Current user is secondary owner:', userIsSecondaryOwner, 'Current user ID:', currentUserId, 'Secondary owner IDs:', secondaryOwnerIds);
        
        // Check if current user is an assignee
        const checkIsAssignee = () => {
          if (!currentUserId || assignedEmployeeIds.length === 0) {
            console.log('Assignee check: No currentUserId or no assigned employees');
            return false;
          }
          const userId = String(currentUserId).trim();
          const isAssigned = assignedEmployeeIds.some(id => {
            const assignedId = String(id).trim();
            return assignedId !== '' && userId !== '' && assignedId === userId;
          });
          console.log('Assignee check:', isAssigned, 'userId:', userId, 'assignedEmployeeIds:', assignedEmployeeIds);
          return isAssigned;
        };
        const userIsAssignee = checkIsAssignee();
        setIsAssignee(userIsAssignee);
        console.log('Current user is assignee:', userIsAssignee, 'Current user ID:', currentUserId, 'Assigned employee IDs:', assignedEmployeeIds);
        
        // Set status form data with the existing status update and issues - Added task type, secondary owners, and completion fields
        setStatusFormData({
          status: formattedTask.status,
          completion: formattedTask.completion,
          comment: formattedTask.statusUpdateDesc || '',
          issues: formattedTask.issues || '',
          assignedTo: assignedEmployeeIds,
          priority: formattedTask.priority,
          dueDate: formattedTask.dueDate || '',
          taskOwner: formattedTask.taskOwner,
          taskType: formattedTask.taskType, // Added task type
          secondaryOwners: secondaryOwnerIds, // Added secondary owners
          title: formattedTask.title || '',
          description: formattedTask.description || '',
          compTypeId: String(formattedTask.compTypeId || ''), // Use completion type ID directly from backend
          compStatusTextDesc: formattedTask.compStatusTextDesc || '', // Completion text description
          releaseType: formattedTask.releaseType || '',
          releaseDate: formattedTask.releaseDate || '',
        });
        
        // Set task form data with ALL fields including status fields - Added task type, secondary owners, and completion fields
        setTaskFormData({
          title: formattedTask.title,
          description: formattedTask.description,
          assignedTo: assignedEmployeeIds,
          application: formattedTask.applicationId,
          applicationId: formattedTask.applicationId,
          startDate: formattedTask.startDate,
          dueDate: formattedTask.dueDate,
          priority: formattedTask.priority,
          status: formattedTask.status,
          completion: formattedTask.completion,
          comment: formattedTask.statusUpdateDesc || '',
          issues: formattedTask.issues || '',
          taskOwner: formattedTask.taskOwner,
          taskType: formattedTask.taskType, // Added task type
          secondaryOwners: secondaryOwnerIds, // Added secondary owners
          compTypeId: String(formattedTask.compTypeId || ''), // Use completion type ID directly from backend
          compStatusTextDesc: formattedTask.compStatusTextDesc || '', // Completion text description
          releaseType: formattedTask.releaseType || '',
          releaseDate: formattedTask.releaseDate || ''
        });

        // Set completion file info if it exists
        if (formattedTask.compStatusAttachFileName) {
          // Determine if file is an image based on extension and type
          const fileName = formattedTask.compStatusAttachFileName.toLowerCase();
          const fileExtension = fileName.split('.').pop();
          const fileType = (formattedTask.compStatusAttachFileType || '').toLowerCase();
          const isImageFile = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(fileExtension) || 
                             fileType.includes('image') || 
                             fileType === 'png' || 
                             fileType === 'jpg' || 
                             fileType === 'jpeg';
          
          const completionFileObj = {
            name: formattedTask.compStatusAttachFileName,
            type: formattedTask.compStatusAttachFileType || '',
            downloadUrl: formattedTask.compStatusFileDownloadUrl,
            viewUrl: formattedTask.compStatusFileViewUrl,
            objectKey: formattedTask.compStatusFileObjectKey,
            isExisting: true, // Flag to indicate this is an existing file from the server
            isImage: isImageFile // Flag to indicate if this is an image file
          };
          setCompletionFile(completionFileObj);
          completionFileRef.current = completionFileObj;
          console.log('Set existing completion file:', completionFileObj);
        }

        // Log completion fields being loaded
        console.log('Completion fields from API:', {
          compTypeId: taskDetails.aa_app_rl_comp_type_id,
          compTypeName: taskDetails.aa_app_rl_comp_type_name,
          compStatusTextDesc: taskDetails.aa_app_rl_comp_status_text_desc,
          compStatusAttachFileName: taskDetails.aa_app_rl_comp_status_attach_file_name
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading task data:', err);
        ownerSlaHydrationSnapshotRef.current = null;
        statusSlaHydrationSnapshotRef.current = null;
        setIsFunctionalLead(false);
        setError('Failed to load task data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchTaskData();
  }, [taskId, userData.name, currentUserId]);

  // Load completion file preview as blob when viewUrl is available
  useEffect(() => {
    let isCancelled = false;
    let currentBlobUrl = null;

    // Cleanup previous blob URL
    if (completionFilePreviewUrl) {
      window.URL.revokeObjectURL(completionFilePreviewUrl);
      setCompletionFilePreviewUrl(null);
    }

    // Load new preview if completion file exists and is an image
    if (completionFile?.isExisting && completionFile?.isImage && completionFile?.viewUrl) {
      const loadPreview = async () => {
        try {
          const response = await axiosInstance.get(completionFile.viewUrl, {
            responseType: 'blob',
            timeout: 30000
          });
          
          if (isCancelled) return;
          
          const blob = new Blob([response.data], { 
            type: completionFile.type || response.headers['content-type'] || 'image/png'
          });
          
          const blobUrl = window.URL.createObjectURL(blob);
          currentBlobUrl = blobUrl;
          
          if (!isCancelled) {
            setCompletionFilePreviewUrl(blobUrl);
          } else {
            window.URL.revokeObjectURL(blobUrl);
          }
        } catch (error) {
          console.error('Error loading completion file preview:', error);
          // If preview fails to load, set to null so it falls back to icon
          if (!isCancelled) {
            setCompletionFilePreviewUrl(null);
          }
        }
      };
      
      loadPreview();
    } else {
      // If no completion file or not an image, clear preview
      setCompletionFilePreviewUrl(null);
    }

    // Cleanup function
    return () => {
      isCancelled = true;
      if (currentBlobUrl) {
        window.URL.revokeObjectURL(currentBlobUrl);
      }
      if (completionFilePreviewUrl) {
        window.URL.revokeObjectURL(completionFilePreviewUrl);
      }
    };
  }, [completionFile?.viewUrl, completionFile?.isExisting, completionFile?.isImage]);

  // Multi-select helper functions
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <span key={index} className="search-highlight">{part}</span> : 
        part
    );
  };

  // Task Owner handlers for status form
  const handleStatusOwnerSearchChange = (e) => {
    const value = e.target.value;
    setStatusOwnerSearchTerm(value);
    
    if (value === "") {
      setStatusSelectedOwner(null);
      setStatusFormData((prevData) => ({
        ...prevData,
        taskOwner: "",
      }));
    }
  };

  const removeStatusSelectedOwner = () => {
    setStatusSelectedOwner(null);
    setStatusOwnerSearchTerm("");
    setStatusFormData((prevData) => ({
      ...prevData,
      taskOwner: "",
    }));
  };

  const handleStatusOwnerSelect = (employee) => {
    setStatusSelectedOwner(employee);
    setStatusOwnerSearchTerm(employee.name);
    setStatusFormData((prevData) => ({
      ...prevData,
      taskOwner: String(employee.id),
    }));
    setIsStatusOwnerDropdownOpen(false);
  };

  // Task Owner handlers for task form
  const handleTaskOwnerSearchChange = (e) => {
    const value = e.target.value;
    setTaskOwnerSearchTerm(value);
    
    if (value === "") {
      setTaskSelectedOwner(null);
      setTaskFormData((prevData) => ({
        ...prevData,
        taskOwner: "",
      }));
    }
  };

  const removeTaskSelectedOwner = () => {
    setTaskSelectedOwner(null);
    setTaskOwnerSearchTerm("");
    setTaskFormData((prevData) => ({
      ...prevData,
      taskOwner: "",
    }));
  };

  const handleTaskOwnerSelect = (employee) => {
    setTaskSelectedOwner(employee);
    setTaskOwnerSearchTerm(employee.name);
    setTaskFormData((prevData) => ({
      ...prevData,
      taskOwner: String(employee.id),
    }));
    setIsTaskOwnerDropdownOpen(false);
  };

  // Status form multi-select handlers
  const handleStatusSearchChange = (e) => {
    setStatusSearchTerm(e.target.value);
  };

  const handleStatusSearchFocus = () => {
    setIsStatusDropdownOpen(true);
  };

  const clearStatusSearch = () => {
    setStatusSearchTerm("");
    setIsStatusDropdownOpen(false);
  };

  const handleStatusSelectAll = () => {
    const allFilteredSelected = statusFilteredEmployees.every(emp => 
      statusSelectedEmployees.some(selected => selected.id === emp.id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered employees
      const remainingSelected = statusSelectedEmployees.filter(selected => 
        !statusFilteredEmployees.some(filtered => filtered.id === selected.id)
      );
      setStatusSelectedEmployees(remainingSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        assignedTo: remainingSelected.map((emp) => emp.id),
      }));
    } else {
      // Select all filtered employees
      const newSelections = statusFilteredEmployees.filter(emp => 
        !statusSelectedEmployees.some(selected => selected.id === emp.id)
      );
      const updatedSelected = [...statusSelectedEmployees, ...newSelections];
      setStatusSelectedEmployees(updatedSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const handleStatusEmployeeToggle = (employee) => {
    const isSelected = statusSelectedEmployees.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove employee
      const updatedSelected = statusSelectedEmployees.filter((emp) => emp.id !== employee.id);
      setStatusSelectedEmployees(updatedSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add employee
      const updatedSelected = [...statusSelectedEmployees, employee];
      setStatusSelectedEmployees(updatedSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const removeStatusSelectedEmployee = (employeeId) => {
    const updatedSelected = statusSelectedEmployees.filter((emp) => emp.id !== employeeId);
    setStatusSelectedEmployees(updatedSelected);
    setStatusFormData((prevData) => ({
      ...prevData,
      assignedTo: updatedSelected.map((emp) => emp.id),
    }));
  };

  // Task form multi-select handlers
  const handleTaskSearchChange = (e) => {
    setTaskSearchTerm(e.target.value);
  };

  const handleTaskSearchFocus = () => {
    setIsTaskDropdownOpen(true);
  };

  const clearTaskSearch = () => {
    setTaskSearchTerm("");
    setIsTaskDropdownOpen(false);
  };

  const handleTaskSelectAll = () => {
    const allFilteredSelected = taskFilteredEmployees.every(emp => 
      taskSelectedEmployees.some(selected => selected.id === emp.id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered employees
      const remainingSelected = taskSelectedEmployees.filter(selected => 
        !taskFilteredEmployees.some(filtered => filtered.id === selected.id)
      );
      setTaskSelectedEmployees(remainingSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        assignedTo: remainingSelected.map((emp) => emp.id),
      }));
    } else {
      // Select all filtered employees
      const newSelections = taskFilteredEmployees.filter(emp => 
        !taskSelectedEmployees.some(selected => selected.id === emp.id)
      );
      const updatedSelected = [...taskSelectedEmployees, ...newSelections];
      setTaskSelectedEmployees(updatedSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const handleTaskEmployeeToggle = (employee) => {
    const isSelected = taskSelectedEmployees.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove employee
      const updatedSelected = taskSelectedEmployees.filter((emp) => emp.id !== employee.id);
      setTaskSelectedEmployees(updatedSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add employee
      const updatedSelected = [...taskSelectedEmployees, employee];
      setTaskSelectedEmployees(updatedSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        assignedTo: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const removeTaskSelectedEmployee = (employeeId) => {
    const updatedSelected = taskSelectedEmployees.filter((emp) => emp.id !== employeeId);
    setTaskSelectedEmployees(updatedSelected);
    setTaskFormData((prevData) => ({
      ...prevData,
      assignedTo: updatedSelected.map((emp) => emp.id),
    }));
  };

  // Secondary owner handlers for status form
  const handleStatusSecondaryOwnerSearchChange = (e) => {
    setStatusSecondaryOwnerSearchTerm(e.target.value);
  };

  const handleStatusSecondaryOwnerSearchFocus = () => {
    setIsStatusSecondaryOwnerDropdownOpen(true);
  };

  const handleStatusSecondaryOwnerToggle = (employee) => {
    // Prevent task owner from being selected as secondary owner
    const taskOwnerId = statusFormData.taskOwner ? String(statusFormData.taskOwner).trim() : '';
    const employeeId = String(employee.id || employee.uid || '').trim();
    if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Task owner cannot be assigned as a secondary owner.",
        type: "error"
      });
      return;
    }
    
    const isSelected = statusSelectedSecondaryOwners.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove secondary owner
      const updatedSelected = statusSelectedSecondaryOwners.filter((emp) => emp.id !== employee.id);
      setStatusSelectedSecondaryOwners(updatedSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add secondary owner
      const updatedSelected = [...statusSelectedSecondaryOwners, employee];
      setStatusSelectedSecondaryOwners(updatedSelected);
      setStatusFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const removeStatusSecondaryOwner = (employeeId) => {
    const updatedSelected = statusSelectedSecondaryOwners.filter((emp) => emp.id !== employeeId);
    setStatusSelectedSecondaryOwners(updatedSelected);
    setStatusFormData((prevData) => ({
      ...prevData,
      secondaryOwners: updatedSelected.map((emp) => emp.id),
    }));
  };

  // Secondary owner handlers for task form
  const handleTaskSecondaryOwnerSearchChange = (e) => {
    setTaskSecondaryOwnerSearchTerm(e.target.value);
  };

  const handleTaskSecondaryOwnerSearchFocus = () => {
    setIsTaskSecondaryOwnerDropdownOpen(true);
  };

  const handleTaskSecondaryOwnerToggle = (employee) => {
    // Prevent task owner from being selected as secondary owner
    const taskOwnerId = taskFormData.taskOwner ? String(taskFormData.taskOwner).trim() : '';
    const employeeId = String(employee.id || employee.uid || '').trim();
    if (taskOwnerId && employeeId && taskOwnerId === employeeId) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Task owner cannot be assigned as a secondary owner.",
        type: "error"
      });
      return;
    }
    
    const isSelected = taskSelectedSecondaryOwners.some((emp) => emp.id === employee.id);
    
    if (isSelected) {
      // Remove secondary owner
      const updatedSelected = taskSelectedSecondaryOwners.filter((emp) => emp.id !== employee.id);
      setTaskSelectedSecondaryOwners(updatedSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    } else {
      // Add secondary owner
      const updatedSelected = [...taskSelectedSecondaryOwners, employee];
      setTaskSelectedSecondaryOwners(updatedSelected);
      setTaskFormData((prevData) => ({
        ...prevData,
        secondaryOwners: updatedSelected.map((emp) => emp.id),
      }));
    }
  };

  const removeTaskSecondaryOwner = (employeeId) => {
    const updatedSelected = taskSelectedSecondaryOwners.filter((emp) => emp.id !== employeeId);
    setTaskSelectedSecondaryOwners(updatedSelected);
    setTaskFormData((prevData) => ({
      ...prevData,
      secondaryOwners: updatedSelected.map((emp) => emp.id),
    }));
  };
  
  // Helper function to find employee name by ID
  const findEmployeeNameById = (id) => {
    const employee = employees.find(emp => emp.id === id);
    return employee ? employee.name : "Unknown Employee";
  };
  
  // Helper function to format date string for input field (YYYY-MM-DD)
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      // If the date is in format "May 13, 2025"
      if (typeof dateStr === 'string' && dateStr.includes(',')) {
        // Split the date string into parts and construct in a timezone-safe way
        const parts = dateStr.split(', ');
        const datePart = parts[0]; // "May 15"
        const yearPart = parts[1]; // "2025"
        
        const monthDict = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06', 
          'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        
        const monthDay = datePart.split(' ');
        const month = monthDict[monthDay[0].substring(0, 3)];
        const day = monthDay[1].padStart(2, '0');
        
        // Format directly to YYYY-MM-DD
        return `${yearPart}-${month}-${day}`;
      } 
      
      // If already in YYYY-MM-DD format
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateStr;
      }
      
      // If in DD/MM/YYYY format
      if (typeof dateStr === 'string' && dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = dateStr.split('/');
        // Convert to YYYY-MM-DD format
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
      
      // If in MM/DD/YYYY format
      if (typeof dateStr === 'string' && dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = dateStr.split('/');
        // Convert to YYYY-MM-DD format (assuming month is first)
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Fallback for other formats - use a timezone-safe approach
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };
  
  // Helper function to format date string for display
  const formatDateString = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      // Convert all dates to YYYY-MM-DD format for consistency
      return formatDateForInput(dateStr);
    } catch (e) {
      console.error('Error formatting display date:', e);
      return dateStr; // Return as is if parsing fails
    }
  };

  // File upload handlers (available to everyone)
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file: file,
      id: Date.now() + Math.random(), // Unique ID for each file
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setAttachedFiles(prevFiles => {
      const updated = [...prevFiles, ...newFiles];
      attachedFilesRef.current = updated;
      return updated;
    });
    
    // Reset the input
    e.target.value = '';
  };

  // Handle file removal
  const handleRemoveFile = (fileId) => {
    setAttachedFiles(prevFiles => {
      const fileToRemove = prevFiles.find(f => f.id === fileId);
      // Revoke object URL to free memory
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      const updated = prevFiles.filter(f => f.id !== fileId);
      attachedFilesRef.current = updated;
      return updated;
    });
  };

  // Format file size helper
  const formatFileSizeHelper = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle completion file selection (single file)
  const handleCompletionFileChange = (e) => {
    const file = e.target.files[0]; // Only take the first file
    if (file) {
      // Cleanup previous file preview if exists
      if (completionFileRef.current && completionFileRef.current.preview) {
        URL.revokeObjectURL(completionFileRef.current.preview);
      }
      
      const fileObj = {
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };
      
      setCompletionFile(fileObj);
      completionFileRef.current = fileObj;
    }
    
    // Reset the input
    e.target.value = '';
  };

  // Handle completion file removal
  const handleRemoveCompletionFile = () => {
    if (completionFileRef.current && completionFileRef.current.preview) {
      URL.revokeObjectURL(completionFileRef.current.preview);
    }
    setCompletionFile(null);
    completionFileRef.current = null;
  };

  // No hardcoded mappings needed - completion type IDs are fetched from backend and used directly

  // Get file icon based on type or file extension - returns Font Awesome icon class
  const getFileIcon = (type, fileName) => {
    // First try based on MIME type - check Excel BEFORE Word to avoid false matches
    if (type) {
      if (type.includes('image')) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
      if (type.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
      // Check Excel/spreadsheet BEFORE Word (Excel MIME types contain "document" which would match Word)
      if (type.includes('excel') || type.includes('spreadsheet') || type.includes('application/vnd.ms-excel') || type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml') || type.includes('spreadsheetml')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
      // Check PowerPoint before Word too
      if (type.includes('powerpoint') || type.includes('presentation') || type.includes('application/vnd.ms-powerpoint') || type.includes('application/vnd.openxmlformats-officedocument.presentationml')) return <i className="fas fa-file-powerpoint" style={{ fontSize: '20px', color: '#FF9800' }}></i>;
      // Now check Word (but exclude spreadsheet and presentation types)
      if ((type.includes('word') || type.includes('msword') || type.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) && !type.includes('spreadsheet') && !type.includes('presentation')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
      // Generic document check (only if not spreadsheet or presentation)
      if (type.includes('document') && !type.includes('spreadsheet') && !type.includes('presentation')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
      if (type.includes('video')) return <i className="fas fa-file-video" style={{ fontSize: '20px', color: '#9C27B0' }}></i>;
      if (type.includes('audio')) return <i className="fas fa-file-audio" style={{ fontSize: '20px', color: '#FF5722' }}></i>;
      if (type.includes('zip') || type.includes('rar') || type.includes('archive') || type.includes('compressed')) return <i className="fas fa-file-archive" style={{ fontSize: '20px', color: '#795548' }}></i>;
      if (type.includes('csv') || type.includes('text/plain') || type.includes('text/csv')) return <i className="fas fa-file-csv" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
      if (type.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
    }
    
    // Fallback to file extension
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      // Images (including screenshots)
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif', 'heic', 'heif'].includes(extension)) {
        return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
      }
      // PDF
      if (extension === 'pdf') {
        return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
      }
      // Word documents
      if (['doc', 'docx', 'rtf', 'odt'].includes(extension)) {
        return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
      }
      // Excel/spreadsheets
      if (['xls', 'xlsx', 'ods', 'xlsm'].includes(extension)) {
        return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
      }
      // PowerPoint/presentations
      if (['ppt', 'pptx', 'odp'].includes(extension)) {
        return <i className="fas fa-file-powerpoint" style={{ fontSize: '20px', color: '#FF9800' }}></i>;
      }
      // Text files
      if (['txt', 'html', 'htm', 'css', 'js', 'json', 'xml'].includes(extension)) {
        return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
      }
      // CSV files
      if (extension === 'csv') {
        return <i className="fas fa-file-csv" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
      }
      // Archives
      if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) {
        return <i className="fas fa-file-archive" style={{ fontSize: '20px', color: '#795548' }}></i>;
      }
      // Video
      if (['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv', 'flv', 'm4v'].includes(extension)) {
        return <i className="fas fa-file-video" style={{ fontSize: '20px', color: '#9C27B0' }}></i>;
      }
      // Audio
      if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'].includes(extension)) {
        return <i className="fas fa-file-audio" style={{ fontSize: '20px', color: '#FF5722' }}></i>;
      }
    }
    
    // Default file icon
    return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
  };

  // Helper function to extract attachment ID from various possible field names
  // Returns the numeric ID if found, or null if no valid ID is available
  const getAttachmentId = (attachment) => {
    if (!attachment) return null;
    
    // Try multiple possible field names for the attachment ID
    let numericId = null;
    
    // Check aa_app_task_attachment_id (correct field name from backend query)
    if (attachment.aa_app_task_attachment_id !== undefined && attachment.aa_app_task_attachment_id !== null) {
      const id = Number(attachment.aa_app_task_attachment_id);
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    // Check aa_app_task_file_attachment_id (alternative field name)
    if (attachment.aa_app_task_file_attachment_id !== undefined && attachment.aa_app_task_file_attachment_id !== null) {
      const id = Number(attachment.aa_app_task_file_attachment_id);
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    // Check id
    if (attachment.id !== undefined && attachment.id !== null) {
      const id = Number(attachment.id);
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    // Check attachment_id
    if (attachment.attachment_id !== undefined && attachment.attachment_id !== null) {
      const id = Number(attachment.attachment_id);
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    // Check file_attachment_id
    if (attachment.file_attachment_id !== undefined && attachment.file_attachment_id !== null) {
      const id = Number(attachment.file_attachment_id);
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    // Try to find any numeric field that might be an ID
    // Check all fields for numeric values that could be IDs
    for (const [key, value] of Object.entries(attachment)) {
      if (key.toLowerCase().includes('id') && typeof value === 'number' && value > 0) {
        console.log(`📎 Found potential ID in field ${key}:`, value);
        return value;
      }
    }
    
    // If no numeric ID found, log for debugging
    console.warn('📎 No valid numeric attachment ID found in attachment object:', {
      keys: Object.keys(attachment),
      allValues: Object.entries(attachment).map(([key, value]) => ({ key, value, type: typeof value }))
    });
    
    return null;
  };

  // Handle attachment deletion (available to everyone)

  // Cleanup file previews on unmount
  useEffect(() => {
    return () => {
      attachedFilesRef.current.forEach(fileObj => {
        if (fileObj.preview) {
          URL.revokeObjectURL(fileObj.preview);
        }
      });
      if (completionFileRef.current && completionFileRef.current.preview) {
        URL.revokeObjectURL(completionFileRef.current.preview);
      }
    };
  }, []);
  
  // Modified handleBack to preserve previous filters
  const handleBack = () => {
    // Check if there are stored filters in localStorage (using correct key)
    const storedFilters = localStorage.getItem('taskFilters');
    
    if (storedFilters) {
      // Navigate back to all tasks with previous filters
      navigate('/all-tasks', { 
        state: { 
          filters: JSON.parse(storedFilters),
          preserveFilters: true 
        }
      });
    } else {
      // If no stored filters, just navigate to all tasks
      navigate('/all-tasks');
    }
  };
  
  // Helper function to find application ID by name
  const findApplicationIdByName = (name) => {
    const app = applications.find(app => app.aa_app_rl_application_name === name);
    return app ? app.aa_app_rl_application_id : null;
  };

  const canEditTitleAndDescription = isTaskOwner || isFunctionalLead;

  /** Task owner or creator may change task owner and expected due date (status form + owner task form). */
  const canEditTaskOwnerAndDueDate = isTaskOwner || isTaskCreator;

  // Handle input changes for status form
  const handleStatusInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "dueDate") {
      if (!canEditTaskOwnerAndDueDate) return;
      setStatusFormData((prev) => ({ ...prev, dueDate: value }));
      return;
    }
    setStatusFormData((prev) => {
      const next = {
        ...prev,
        [name]: name === "completion" ? parseInt(value, 10) : value,
      };
      if (name === "priority" && task) {
        const appId = task.applicationId;
        const start = task.startDate;
        const pri = next.priority;
        if (
          appId != null &&
          appId !== "" &&
          pri &&
          start &&
          taskSlaDetails.length
        ) {
          const computed = computeDueDateFromSlaWithLookups(
            taskSlaDetails,
            appId,
            pri,
            start,
            applications,
            priorities
          );
          return { ...next, dueDate: computed ?? "" };
        }
      }
      return next;
    });
  };
  
  // Handle input changes for task form (owners only see this form; start date stays fixed from task)
  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") return;
    setTaskFormData((prev) => {
      const next = {
        ...prev,
        [name]: name === "completion" ? parseInt(value, 10) : value,
      };
      if (
        (name === "priority" || name === "application") &&
        taskSlaDetails.length
      ) {
        const app = next.application;
        const pri = next.priority;
        const start = next.startDate;
        if (app && pri && start) {
          const computed = computeDueDateFromSlaWithLookups(
            taskSlaDetails,
            app,
            pri,
            start,
            applications,
            priorities
          );
          return { ...next, dueDate: computed ?? "" };
        }
      }
      return next;
    });
  };

  const handleEditableTitleDescriptionChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (name !== "title" && name !== "description") return;
      if (!canEditTitleAndDescription) return;
      if (isTaskOwner) {
        setTaskFormData((prev) => ({ ...prev, [name]: value }));
      } else if (isFunctionalLead) {
        setStatusFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    [canEditTitleAndDescription, isTaskOwner, isFunctionalLead]
  );

  // Owner task form: SLA due only after user changes application, priority, or start (preserve API due on load)
  useEffect(() => {
    const snap = ownerSlaHydrationSnapshotRef.current;
    if (!snap || snap.loading) return;

    const app = taskFormData.application;
    const pri = taskFormData.priority;
    const start = taskFormData.startDate;

    const stillMatchesLoadedTask =
      String(app ?? "") === String(snap.applicationId ?? "") &&
      String(pri ?? "") === String(snap.priorityId ?? "") &&
      String(start ?? "") === String(snap.startDate ?? "");

    if (stillMatchesLoadedTask) {
      return;
    }

    if (!app || !pri || !start) {
      setTaskFormData((prev) => (prev.dueDate === "" ? prev : { ...prev, dueDate: "" }));
      return;
    }
    if (!taskSlaDetails.length) {
      return;
    }
    const computed = computeDueDateFromSlaWithLookups(
      taskSlaDetails,
      app,
      pri,
      start,
      applications,
      priorities
    );
    setTaskFormData((prev) => {
      if (!computed) return prev;
      return prev.dueDate === computed ? prev : { ...prev, dueDate: computed };
    });
  }, [
    taskFormData.application,
    taskFormData.priority,
    taskFormData.startDate,
    taskSlaDetails,
    applications,
    priorities,
  ]);

  // Non-owner status form: SLA due only when priority diverges from loaded task (preserve API due on load)
  useEffect(() => {
    if (!task) return;
    const snap = statusSlaHydrationSnapshotRef.current;
    if (!snap || snap.loading) return;

    const appId = task.applicationId;
    const pri = statusFormData.priority;
    const start = task.startDate;

    const stillMatchesLoadedTask =
      String(appId ?? "") === String(snap.applicationId ?? "") &&
      String(pri ?? "") === String(snap.priorityId ?? "") &&
      String(start ?? "") === String(snap.startDate ?? "");

    if (stillMatchesLoadedTask) {
      return;
    }

    if (appId == null || appId === "" || !pri || !start) {
      setStatusFormData((prev) => (prev.dueDate === "" ? prev : { ...prev, dueDate: "" }));
      return;
    }
    if (!taskSlaDetails.length) {
      return;
    }
    const computed = computeDueDateFromSlaWithLookups(
      taskSlaDetails,
      appId,
      pri,
      start,
      applications,
      priorities
    );
    setStatusFormData((prev) => {
      if (!computed) return prev;
      return prev.dueDate === computed ? prev : { ...prev, dueDate: computed };
    });
  }, [task, statusFormData.priority, taskSlaDetails, applications, priorities]);

  /** Enter must not submit forms; use the Submit button. Textareas keep Enter for new lines. */
  const suppressEnterSubmit = useCallback((e) => {
    if (e.key !== "Enter") return;
    if (e.nativeEvent?.isComposing) return;
    if (e.target && String(e.target.tagName).toUpperCase() === "TEXTAREA") return;
    e.preventDefault();
  }, []);
  
  // Handle submit for status update (for non-owners) - UPDATED to include task owner and task type
  const handleStatusSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    // Validate that a task owner is selected
    if (!statusFormData.taskOwner) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task owner.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    const statusAppId =
      task?.applicationId != null && task.applicationId !== ""
        ? String(task.applicationId)
        : "";
    const statusMismatchMsgs = getApplicationMismatchMessages(
      statusAppId,
      statusSelectedOwner,
      statusSelectedSecondaryOwners,
      statusSelectedEmployees
    );
    if (statusMismatchMsgs.length > 0) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: statusMismatchMsgs.join(" "),
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate that a task type is selected
    if (!statusFormData.taskType) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task type.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate completion fields if status is "completed"
    if (isStatusCompleted(statusFormData.status)) {
      console.log("Status is completed, checking compTypeId:", statusFormData.compTypeId);
      if (!statusFormData.compTypeId || statusFormData.compTypeId === '' || statusFormData.compTypeId === '0') {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please select a completion type. The completion type field is required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
      if (!statusFormData.compStatusTextDesc || !statusFormData.compStatusTextDesc.trim()) {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please enter completion notes. Completion notes are required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
    }

    if (
      !isStatusCompletedOrCancelled(statusFormData.status) &&
      !statusFormData.dueDate
    ) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message:
          "Expected due date could not be calculated from SLA. Verify application, priority, and SLA configuration.",
        type: "error",
      });
      setIsSubmitting(false);
      return;
    }

    const statusSaveTitle = (statusFormData.title ?? task.title ?? "").trim();
    if (!statusSaveTitle) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please enter a task title.",
        type: "error",
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      const formattedStartDate =
        task.startDate || toDateTimeLocalInputValue(task.creationDate);
      
      // Prepare data for API using the new format - UPDATED to include task owner, task type, and secondary owners
      const updateData = {
        aa_app_rl_resource_task_uid: parseInt(taskId),
        aa_app_rl_task_status_id: statusFormData.status,
        aa_app_rl_task_completion_percentage: statusFormData.completion,
        aa_app_rl_task_status_update_desc:statusFormData.comment,
        aa_app_rl_task_status_issue_desc: statusFormData.issues,
        // Title/description are editable for functional lead via statusFormData; otherwise unchanged from task
        aa_app_rl_resource_task_title: statusSaveTitle,
        aa_app_rl_resource_task_desc:
          statusFormData.description ?? task.description ?? "",
        aa_app_rl_application_id: task.applicationId,
        aa_app_rl_application_functional_lead_id: resolveApplicationFunctionalLeadId(
          task.applicationId,
          applications
        ),
        aa_app_task_assigned_to: statusFormData.assignedTo.map(id => parseInt(id)), // Array of employee UIDs
        aa_app_task_owner_id: parseInt(statusFormData.taskOwner), // Task owner UID
        aa_app_task_secondary_owners: statusFormData.secondaryOwners && statusFormData.secondaryOwners.length > 0 
          ? statusFormData.secondaryOwners.map(id => parseInt(id)) 
          : [], // Array of secondary owner UIDs
        aa_app_rl_resource_task_priority_id: parseInt(statusFormData.priority),
        aa_app_rl_task_type_id: parseInt(statusFormData.taskType), // Added task type
        aa_app_task_start_date: formattedStartDate,
        aa_app_task_due_date: statusFormData.dueDate,
        aa_app_task_created_by: task.createdBy,
        aa_app_task_updated_by: currentUserId
      };

      if (statusFormData.releaseType) {
        updateData.aa_app_rl_release_type_id = parseInt(statusFormData.releaseType, 10);
      }
      if (statusFormData.releaseDate) {
        updateData.aa_app_rl_release_date = statusFormData.releaseDate;
      }

      // Always include completion fields when status is completed
      if (isStatusCompleted(statusFormData.status)) {
        updateData.aa_app_rl_comp_type_id = parseInt(statusFormData.compTypeId) || 0;
        updateData.aa_app_rl_comp_status_text_desc = statusFormData.compStatusTextDesc || null;
        console.log("Adding completion fields to payload:", {
          comp_type_id: updateData.aa_app_rl_comp_type_id,
          comp_status_text_desc: updateData.aa_app_rl_comp_status_text_desc
        });
      }
      
      // Handle file uploads if files are attached (available to everyone)
      let hasFiles = attachedFiles.length > 0;
      // Handle completion file if status is completed
      let hasCompletionFile = isStatusCompleted(statusFormData.status) && completionFile !== null;
      
      // Log completion fields being sent
      if (isStatusCompleted(statusFormData.status)) {
        console.log("Completion fields in payload:", {
          aa_app_rl_comp_type_id: updateData.aa_app_rl_comp_type_id,
          aa_app_rl_comp_status_text_desc: updateData.aa_app_rl_comp_status_text_desc,
          completion_file: hasCompletionFile ? completionFile.name : "No file"
        });
      }
      
      let payload = updateData;
      
      // Use FormData if there are files
      if (hasFiles || hasCompletionFile) {
        const formDataObj = new FormData();
        
        // Append task data as JSON string (API expects 'taskData' key)
        formDataObj.append('taskData', JSON.stringify(updateData));
        
        // Append regular files (if any)
        if (hasFiles) {
          attachedFiles.forEach((fileObj) => {
            formDataObj.append('attachments', fileObj.file);
            formDataObj.append('attachment_names', fileObj.name);
          });
        }
        
        // Append completion file (single file - API expects 'completion_file')
        if (hasCompletionFile) {
          formDataObj.append('completion_file', completionFile.file);
        }
        
        payload = formDataObj;
      }
      
      // Call API to update task status using the new endpoint
      // Note: TaskService.updateTaskStatus may need to be updated to handle FormData
      const response = await TaskService.updateTaskStatus(taskId, payload);
      
      await refreshNotificationCount(undefined, { force: true });
      setDialog({
        isOpen: true,
        title: "Success",
        message: formatTaskSuccessMessage(response, "Task status updated successfully!", taskId),
        type: "success",
        onClose: () => {
          setDialog({ ...dialog, isOpen: false });
          handleBack();
        }
      });
    } catch (err) {
      console.error('Error updating task status:', err);
      setDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to update task status. Please try again.",
        type: "error"
      });
      setIsSubmitting(false);
    }
  };
  
  // Handle submit for task details update (for owners) - UPDATED to include task owner and task type
  const handleTaskSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    // Validate that a task owner is selected
    if (!taskFormData.taskOwner) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task owner.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    if (!(taskFormData.title ?? "").trim()) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please enter a task title.",
        type: "error",
      });
      setIsSubmitting(false);
      return;
    }

    const taskMismatchMsgs = getApplicationMismatchMessages(
      taskFormData.application,
      taskSelectedOwner,
      taskSelectedSecondaryOwners,
      taskSelectedEmployees
    );
    if (taskMismatchMsgs.length > 0) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: taskMismatchMsgs.join(" "),
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate that a task type is selected
    if (!taskFormData.taskType) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please select a task type.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    // Validate completion fields if status is "completed"
    if (isStatusCompleted(taskFormData.status)) {
      console.log("Status is completed, checking compTypeId:", taskFormData.compTypeId);
      if (!taskFormData.compTypeId || taskFormData.compTypeId === '' || taskFormData.compTypeId === '0') {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please select a completion type. The completion type field is required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
      if (!taskFormData.compStatusTextDesc || !taskFormData.compStatusTextDesc.trim()) {
        setDialog({
          isOpen: true,
          title: "Validation Error",
          message: "Please enter completion notes. Completion notes are required when status is 'Completed'.",
          type: "error"
        });
        setIsSubmitting(false);
        return;
      }
    }

    if (!taskFormData.startDate) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message: "Please set the task start date and time.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }

    if (!taskFormData.dueDate) {
      setDialog({
        isOpen: true,
        title: "Validation Error",
        message:
          "Expected due date could not be calculated from SLA. Set start date/time and verify application, priority, and SLA configuration.",
        type: "error"
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Prepare data for API using the new structure - UPDATED to include task owner, task type, and secondary owners
      const updateData = {
        aa_app_rl_resource_task_uid: parseInt(taskId),
        aa_app_rl_resource_task_title: taskFormData.title,
        aa_app_rl_resource_task_desc: taskFormData.description,
        aa_app_rl_application_id: parseInt(taskFormData.application),
        aa_app_rl_application_functional_lead_id: resolveApplicationFunctionalLeadId(
          taskFormData.application,
          applications
        ),
        aa_app_task_assigned_to: taskFormData.assignedTo.map(id => parseInt(id)), // Array of employee UIDs
        aa_app_task_owner_id: parseInt(taskFormData.taskOwner), // Task owner UID
        aa_app_task_secondary_owners: taskFormData.secondaryOwners && taskFormData.secondaryOwners.length > 0 
          ? taskFormData.secondaryOwners.map(id => parseInt(id)) 
          : [], // Array of secondary owner UIDs
        aa_app_rl_resource_task_priority_id: parseInt(taskFormData.priority),
        aa_app_rl_task_type_id: parseInt(taskFormData.taskType), // Added task type
        aa_app_task_start_date: taskFormData.startDate,
        aa_app_task_due_date: taskFormData.dueDate,
        aa_app_rl_task_status_id: taskFormData.status,
        aa_app_rl_task_completion_percentage: taskFormData.completion,
        aa_app_rl_task_status_update_desc: taskFormData.comment,
        aa_app_rl_task_status_issue_desc: taskFormData.issues,
        aa_app_task_created_by: task.createdBy,
        aa_app_task_updated_by: currentUserId
      };

      if (taskFormData.releaseType) {
        updateData.aa_app_rl_release_type_id = parseInt(taskFormData.releaseType, 10);
      }
      if (taskFormData.releaseDate) {
        updateData.aa_app_rl_release_date = taskFormData.releaseDate;
      }

      // Always include completion fields when status is completed
      if (isStatusCompleted(taskFormData.status)) {
        updateData.aa_app_rl_comp_type_id = parseInt(taskFormData.compTypeId) || 0;
        updateData.aa_app_rl_comp_status_text_desc = taskFormData.compStatusTextDesc || null;
        console.log("Adding completion fields to payload:", {
          comp_type_id: updateData.aa_app_rl_comp_type_id,
          comp_status_text_desc: updateData.aa_app_rl_comp_status_text_desc
        });
      }
      
      // Handle file uploads if files are attached (available to everyone)
      let hasFiles = attachedFiles.length > 0;
      // Handle completion file if status is completed
      let hasCompletionFile = isStatusCompleted(taskFormData.status) && completionFile !== null;
      
      // Log completion fields being sent
      if (isStatusCompleted(taskFormData.status)) {
        console.log("Completion fields in payload:", {
          aa_app_rl_comp_type_id: updateData.aa_app_rl_comp_type_id,
          aa_app_rl_comp_status_text_desc: updateData.aa_app_rl_comp_status_text_desc,
          completion_file: hasCompletionFile ? completionFile.name : "No file"
        });
      }
      
      let payload = updateData;
      
      // Use FormData if there are files
      if (hasFiles || hasCompletionFile) {
        const formDataObj = new FormData();
        
        // Append task data as JSON string (API expects 'taskData' key)
        formDataObj.append('taskData', JSON.stringify(updateData));
        
        // Append regular files (if any)
        if (hasFiles) {
          attachedFiles.forEach((fileObj) => {
            formDataObj.append('attachments', fileObj.file);
            formDataObj.append('attachment_names', fileObj.name);
          });
        }
        
        // Append completion file (single file - API expects 'completion_file')
        if (hasCompletionFile) {
          formDataObj.append('completion_file', completionFile.file);
        }
        
        payload = formDataObj;
      }
      
      // Call API to update task
      // Note: TaskService.updateTask may need to be updated to handle FormData
      const response = await TaskService.updateTask(taskId, payload);
      
      await refreshNotificationCount(undefined, { force: true });
      setDialog({
        isOpen: true,
        title: "Success",
        message: formatTaskSuccessMessage(response, "Task details updated successfully!", taskId),
        type: "success",
        onClose: () => {
          setDialog({ ...dialog, isOpen: false });
          handleBack();
        }
      });
    } catch (err) {
      console.error('Error updating task details:', err);
      setDialog({
        isOpen: true,
        title: "Error",
        message: "Failed to update task details. Please try again.",
        type: "error"
      });
      setIsSubmitting(false);
    }
  };

   const renderStatusOwnerDropdown = () => {
    return (
      <div className="form-group">
        <label htmlFor="statusTaskOwner">
          Task Owner <span className="required-asterisk">*</span>
        </label>
        
        {/* Task Owner Dropdown - Status Form - Editable for task owner or creator */}
        {canEditTaskOwnerAndDueDate ? (
          <div className={`owner-dropdown-container status-owner-dropdown-container${statusApplicationEmployeeMismatch.ownerInvalid ? ' owner-dropdown-container--invalid' : ''}`} style={{ position: 'relative' }}>
            <div className="search-container">
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search and select task owner..."
                value={statusOwnerSearchTerm}
                onChange={handleStatusOwnerSearchChange}
                onFocus={() => setIsStatusOwnerDropdownOpen(true)}
              />
              {statusSelectedOwner && (
                <button
                  type="button"
                  className="clear-selection"
                  onClick={removeStatusSelectedOwner}
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    color: '#666',
                    cursor: 'pointer',
                    padding: '2px'
                  }}
                  aria-label="Clear selection"
                >
                  ×
                </button>
              )}
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setIsStatusOwnerDropdownOpen(!isStatusOwnerDropdownOpen)}
              >
                <span className={`dropdown-arrow ${isStatusOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
            </div>

            {/* Owner Dropdown Menu */}
            {isStatusOwnerDropdownOpen && (
              <div className="dropdown-menu" style={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {statusFilteredOwners.length > 0 ? (
                  statusFilteredOwners
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((employee) => {
                      const isSelected = statusSelectedOwner && statusSelectedOwner.id === employee.id;
                      return (
                        <div
                          key={employee.id}
                          className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleStatusOwnerSelect(employee)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = isSelected ? '#e3f2fd' : 'transparent'}
                        >
                          <span>
                            {highlightSearchTerm(employee.name, statusOwnerSearchTerm)}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div style={{ padding: '8px 12px', color: '#666' }}>
                    No employees found
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Read-only display for non-task owners */
          <div className="form-input" style={{ 
            backgroundColor: '#f3f4f6', 
            cursor: 'not-allowed',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            color: '#6b7280'
          }}>
            {statusSelectedOwner ? statusSelectedOwner.name : 'No task owner assigned'}
          </div>
        )}
      </div>
    );
  };

   const renderTaskOwnerDropdown = () => {
    return (
      <div className="form-group">
        <label htmlFor="taskTaskOwner">
          Task Owner <span className="required-asterisk">*</span>
        </label>
        
        {/* Task Owner Dropdown - Task Form - Editable for task owner or creator */}
        {canEditTaskOwnerAndDueDate ? (
          <div className={`owner-dropdown-container task-owner-dropdown-container${taskApplicationEmployeeMismatch.ownerInvalid ? ' owner-dropdown-container--invalid' : ''}`} style={{ position: 'relative' }}>
            <div className="search-container">
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search and select task owner..."
                value={taskOwnerSearchTerm}
                onChange={handleTaskOwnerSearchChange}
                onFocus={() => setIsTaskOwnerDropdownOpen(true)}
              />
              {taskSelectedOwner && (
                <button
                  type="button"
                  className="clear-selection"
                  onClick={removeTaskSelectedOwner}
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    color: '#666',
                    cursor: 'pointer',
                    padding: '2px'
                  }}
                  aria-label="Clear selection"
                >
                  ×
                </button>
              )}
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setIsTaskOwnerDropdownOpen(!isTaskOwnerDropdownOpen)}
              >
                <span className={`dropdown-arrow ${isTaskOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
            </div>

            {/* Owner Dropdown Menu */}
            {isTaskOwnerDropdownOpen && (
              <div className="dropdown-menu" style={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {taskFilteredOwners.length > 0 ? (
                  taskFilteredOwners
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((employee) => {
                      const isSelected = taskSelectedOwner && taskSelectedOwner.id === employee.id;
                      return (
                        <div
                          key={employee.id}
                          className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleTaskOwnerSelect(employee)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = isSelected ? '#e3f2fd' : 'transparent'}
                        >
                          <span>
                            {highlightSearchTerm(employee.name, taskOwnerSearchTerm)}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div style={{ padding: '8px 12px', color: '#666' }}>
                    No employees found
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Read-only display for non-task owners */
          <div className="form-input" style={{ 
            backgroundColor: '#f3f4f6', 
            cursor: 'not-allowed',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            color: '#6b7280'
          }}>
            {taskSelectedOwner ? taskSelectedOwner.name : 'No task owner assigned'}
          </div>
        )}
      </div>
    );
  };

  // Multi-select component for status form
  const renderStatusMultiSelect = () => {
    const allFilteredSelected = statusFilteredEmployees.length > 0 && statusFilteredEmployees.every(emp => 
      statusSelectedEmployees.some(selected => selected.id === emp.id)
    );
    const someFilteredSelected = statusFilteredEmployees.some(emp => 
      statusSelectedEmployees.some(selected => selected.id === emp.id)
    );

    return (
      <div className="form-group">
        <label htmlFor="statusAssignedTo">Assigned To</label>
        
        {/* Selected employees display */}
        {statusSelectedEmployees.length > 0 && (
          <div className="selected-employees">
            {statusSelectedEmployees.map((employee) => (
              <span
                key={employee.id}
                className={`employee-tag${
                  statusApplicationEmployeeMismatch.assignedInvalidIds.has(employee.id)
                    ? ' employee-tag--invalid'
                    : ''
                }`}
              >
                {employee.name}
                <button
                  type="button"
                  className="remove-employee"
                  onClick={() => removeStatusSelectedEmployee(employee.id)}
                  aria-label={`Remove ${employee.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Multi-select dropdown with search */}
        <div className="multi-select-dropdown status-multi-select-dropdown">
          {/* Search input with dropdown trigger */}
          <div className="search-container">
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search employees..."
              value={statusSearchTerm}
              onChange={handleStatusSearchChange}
              onFocus={handleStatusSearchFocus}
            />
            <button
              type="button"
              className="dropdown-trigger"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              <span className={`dropdown-arrow ${isStatusDropdownOpen ? 'open' : ''}`}>▼</span>
            </button>
          </div>

          {/* Dropdown options */}
          {isStatusDropdownOpen && (
            <div className="dropdown-menu">
              {/* Select All option */}
              {statusFilteredEmployees.length > 0 && (
                <div className="dropdown-option select-all-option" onClick={handleStatusSelectAll}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={input => {
                      if (input) input.indeterminate = someFilteredSelected && !allFilteredSelected;
                    }}
                    onChange={() => {}} // Handled by parent onClick
                  />
                  <span className="option-text">
                    Select All ({statusFilteredEmployees.length})
                  </span>
                </div>
              )}

             {/* Employee options */}
             {statusFilteredEmployees.length > 0 ? (
                statusFilteredEmployees
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((employee) => {
                    const isSelected = statusSelectedEmployees.some(
                      (emp) => emp.id === employee.id
                    );
                    return (
                      <div
                        key={employee.id}
                        className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleStatusEmployeeToggle(employee)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by parent onClick
                        />
                        <span className="option-text">
                          {highlightSearchTerm(employee.name, statusSearchTerm)}
                        </span>
                      </div>
                    );
                  })
              ) : (
                <div className="no-results">
                  No employees found
                </div>
              )}
            </div>
          )}
        </div>

       {/* Hidden inputs for form submission */}
       {statusFormData.assignedTo.map((employeeId, index) => (
          <input
            key={index}
            type="hidden"
            name={`statusAssignedTo[${index}]`}
            value={employeeId}
          />
        ))}
      </div>
    );
  };

  // Secondary owners component for status form
  const renderStatusSecondaryOwners = () => {
    return (
      <div className="form-group full-width">
        <label htmlFor="statusSecondaryOwners">Secondary Owners</label>
        
        {/* Selected secondary owners display */}
        {statusSelectedSecondaryOwners.length > 0 && (
          <div className="selected-employees">
            {statusSelectedSecondaryOwners.map((employee) => (
              <span key={employee.id} className="employee-tag">
                {employee.name}
                {isTaskOwner && (
                  <button
                    type="button"
                    className="remove-employee"
                    onClick={() => removeStatusSecondaryOwner(employee.id)}
                    aria-label={`Remove ${employee.name}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Multi-select dropdown with search - only editable for task owners */}
        {isTaskOwner ? (
          <div className="multi-select-dropdown status-secondary-owner-dropdown">
            {/* Search input with dropdown trigger */}
            <div className="search-container">
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search employees..."
                value={statusSecondaryOwnerSearchTerm}
                onChange={handleStatusSecondaryOwnerSearchChange}
                onFocus={handleStatusSecondaryOwnerSearchFocus}
              />
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setIsStatusSecondaryOwnerDropdownOpen(!isStatusSecondaryOwnerDropdownOpen)}
              >
                <span className={`dropdown-arrow ${isStatusSecondaryOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
            </div>

            {/* Dropdown options */}
            {isStatusSecondaryOwnerDropdownOpen && (
              <div className="dropdown-menu">
                {/* Employee options */}
                {statusFilteredSecondaryOwners.length > 0 ? (
                  statusFilteredSecondaryOwners
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((employee) => {
                      const isSelected = statusSelectedSecondaryOwners.some(
                        (emp) => emp.id === employee.id
                      );
                      return (
                        <div
                          key={employee.id}
                          className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleStatusSecondaryOwnerToggle(employee)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by parent onClick
                          />
                          <span className="option-text">
                            {highlightSearchTerm(employee.name, statusSecondaryOwnerSearchTerm)}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div className="no-results">
                    No employees found
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Read-only display for non-task owners */
          <div className="form-input" style={{ 
            backgroundColor: '#f3f4f6', 
            cursor: 'not-allowed',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            color: '#6b7280'
          }}>
            {statusSelectedSecondaryOwners.length > 0 
              ? statusSelectedSecondaryOwners.map(emp => emp.name).join(', ')
              : 'No secondary owners assigned'
            }
          </div>
        )}

        {/* Hidden inputs for form submission */}
        {statusFormData.secondaryOwners.map((employeeId, index) => (
          <input
            key={index}
            type="hidden"
            name={`statusSecondaryOwners[${index}]`}
            value={employeeId}
          />
        ))}
      </div>
    );
  };

  // Multi-select component for task form
  const renderTaskMultiSelect = () => {
    const allFilteredSelected = taskFilteredEmployees.length > 0 && taskFilteredEmployees.every(emp => 
      taskSelectedEmployees.some(selected => selected.id === emp.id)
    );
    const someFilteredSelected = taskFilteredEmployees.some(emp => 
      taskSelectedEmployees.some(selected => selected.id === emp.id)
    );

    return (
      <div className="form-group">
        <label htmlFor="taskAssignedTo">Assigned To</label>
        
        {/* Selected employees display */}
        {taskSelectedEmployees.length > 0 && (
          <div className="selected-employees">
            {taskSelectedEmployees.map((employee) => (
              <span
                key={employee.id}
                className={`employee-tag${
                  taskApplicationEmployeeMismatch.assignedInvalidIds.has(employee.id)
                    ? ' employee-tag--invalid'
                    : ''
                }`}
              >
                {employee.name}
                <button
                  type="button"
                  className="remove-employee"
                  onClick={() => removeTaskSelectedEmployee(employee.id)}
                  aria-label={`Remove ${employee.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Multi-select dropdown with search */}
        <div className="multi-select-dropdown task-multi-select-dropdown">
          {/* Search input with dropdown trigger */}
          <div className="search-container">
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search employees..."
              value={taskSearchTerm}
              onChange={handleTaskSearchChange}
              onFocus={handleTaskSearchFocus}
            />
            <button
              type="button"
              className="dropdown-trigger"
              onClick={() => setIsTaskDropdownOpen(!isTaskDropdownOpen)}
            >
              <span className={`dropdown-arrow ${isTaskDropdownOpen ? 'open' : ''}`}>▼</span>
            </button>
          </div>

          {/* Dropdown options */}
          {isTaskDropdownOpen && (
            <div className="dropdown-menu">
              {/* Employee options */}
              {taskFilteredEmployees.length > 0 ? (
                taskFilteredEmployees
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((employee) => {
                    const isSelected = taskSelectedEmployees.some(
                      (emp) => emp.id === employee.id
                    );
                    return (
                      <div
                        key={employee.id}
                        className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleTaskEmployeeToggle(employee)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by parent onClick
                        />
                        <span className="option-text">
                          {highlightSearchTerm(employee.name, taskSearchTerm)}
                        </span>
                      </div>
                    );
                  })
              ) : (
                <div className="no-results">
                  No employees found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden inputs for form submission */}
        {taskFormData.assignedTo.map((employeeId, index) => (
          <input
            key={index}
            type="hidden"
            name={`taskAssignedTo[${index}]`}
            value={employeeId}
          />
        ))}
      </div>
    );
  };

  // Secondary owners component for task form
  const renderTaskSecondaryOwners = () => {
    return (
      <div className="form-group full-width">
        <label htmlFor="taskSecondaryOwners">Secondary Owners</label>
        
        {/* Selected secondary owners display */}
        {taskSelectedSecondaryOwners.length > 0 && (
          <div className="selected-employees">
            {taskSelectedSecondaryOwners.map((employee) => (
              <span
                key={employee.id}
                className={`employee-tag${
                  taskApplicationEmployeeMismatch.secondaryInvalidIds.has(employee.id)
                    ? ' employee-tag--invalid'
                    : ''
                }`}
              >
                {employee.name}
                {isTaskOwner && (
                  <button
                    type="button"
                    className="remove-employee"
                    onClick={() => removeTaskSecondaryOwner(employee.id)}
                    aria-label={`Remove ${employee.name}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Multi-select dropdown with search - only editable for task owners */}
        {isTaskOwner ? (
          <div className="multi-select-dropdown task-secondary-owner-dropdown">
            {/* Search input with dropdown trigger */}
            <div className="search-container">
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search employees..."
                value={taskSecondaryOwnerSearchTerm}
                onChange={handleTaskSecondaryOwnerSearchChange}
                onFocus={handleTaskSecondaryOwnerSearchFocus}
              />
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setIsTaskSecondaryOwnerDropdownOpen(!isTaskSecondaryOwnerDropdownOpen)}
              >
                <span className={`dropdown-arrow ${isTaskSecondaryOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
            </div>

            {/* Dropdown options */}
            {isTaskSecondaryOwnerDropdownOpen && (
              <div className="dropdown-menu">
                {/* Employee options */}
                {taskFilteredSecondaryOwners.length > 0 ? (
                  taskFilteredSecondaryOwners
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((employee) => {
                      const isSelected = taskSelectedSecondaryOwners.some(
                        (emp) => emp.id === employee.id
                      );
                      return (
                        <div
                          key={employee.id}
                          className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleTaskSecondaryOwnerToggle(employee)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by parent onClick
                          />
                          <span className="option-text">
                            {highlightSearchTerm(employee.name, taskSecondaryOwnerSearchTerm)}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div className="no-results">
                    No employees found
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Read-only display for non-task owners */
          <div className="form-input" style={{ 
            backgroundColor: '#f3f4f6', 
            cursor: 'not-allowed',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            color: '#6b7280'
          }}>
            {taskSelectedSecondaryOwners.length > 0 
              ? taskSelectedSecondaryOwners.map(emp => emp.name).join(', ')
              : 'No secondary owners assigned'
            }
          </div>
        )}

        {/* Hidden inputs for form submission */}
        {taskFormData.secondaryOwners.map((employeeId, index) => (
          <input
            key={index}
            type="hidden"
            name={`taskSecondaryOwners[${index}]`}
            value={employeeId}
          />
        ))}
      </div>
    );
  };
  
  // Show loading state while data is loading
  if (loading) {
    return (
      <div className="update-task-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading task...</p>
        </div>
      </div>
    );
  }
  
  // Show error if loading fails
  if (error) {
    return (
      <div className="update-task-screen">
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!task) {
    return (
      <div className="update-task-screen">
        <div className="error-message">
          <p>Task not found. The task may have been deleted or you don't have permission to view it.</p>
          <button 
            className="btn btn-primary"
            onClick={handleBack}
          >
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }
  
  // Get status name from status ID
  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.aa_app_rl_task_status_id === parseInt(statusId));
    return status ? status.aa_app_rl_task_status_name : 'Unknown';
  };
  
  // Get priority name from priority ID
  const getPriorityName = (priorityId) => {
    const priority = priorities.find(p => p.aa_app_rl_priority_id === parseInt(priorityId));
    return priority ? priority.aa_app_rl_priority_name : 'Unknown';
  };
  
  // Render the status history section if available
  const renderStatusHistory = () => {
    if (isLoadingHistory) {
      return <div className="status-history-loading">Loading history...</div>;
    }
    
    if (statusHistory && statusHistory.length > 0) {
      return (
        <div className="status-history">
          <h4>Status Update History</h4>
          <div className="history-entries">
            {statusHistory.map((entry, index) => (
              <div key={index} className="history-entry">
                <div className="history-entry-header">
                  <span className="history-date">{formatDateString(entry.update_date)}</span>
                  <span className="history-status">{entry.status_name} - {entry.completion_percentage}%</span>
                </div>
                {entry.status_update_desc && (
                  <div className="history-comment">
                    <strong>Update:</strong> {entry.status_update_desc}
                  </div>
                )}
                {entry.status_issue_desc && (
                  <div className="history-issues">
                    <strong>Issues:</strong> {entry.status_issue_desc}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Render the non-owner view (status update only) - UPDATED to include task type instead of completion percentage in same row
  const renderStatusUpdateView = () => {
    return (
      <div className="update-form-container status-update-form">
        <h3 className="form-title">Update Task Status</h3>
        {statusApplicationEmployeeMismatch.messages.length > 0 && (
          <div className="application-mismatch-banner" role="alert">
            {statusApplicationEmployeeMismatch.messages.map((msg, idx) => (
              <div key={idx}>{msg}</div>
            ))}
          </div>
        )}
        <form onSubmit={handleStatusSubmit} onKeyDown={suppressEnterSubmit} style={{ width: '100%' }}>
          {/* Task Owner and Task Type in same row */}
          <div className="form-row">
            {/* Task Owner dropdown for status form */}
            {renderStatusOwnerDropdown()}
            
            <div className="form-group">
              <label htmlFor="taskType">
                Task Type <span className="required-asterisk">*</span>
              </label>
              <select 
                id="taskType"
                name="taskType"
                className="form-select"
                value={statusFormData.taskType}
                onChange={handleStatusInputChange}
                required
              >
                <option value="">Select a task type</option>
                {taskTypes
                  .slice()
                  .sort((a, b) => a.aa_app_rl_task_type_longname.localeCompare(b.aa_app_rl_task_type_longname))
                  .map((taskType) => (
                    <option 
                      key={taskType.aa_app_rl_task_type_id} 
                      value={taskType.aa_app_rl_task_type_id}
                    >
                      {taskType.aa_app_rl_task_type_longname}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="statusReleaseType">Release type</label>
              <select
                id="statusReleaseType"
                name="releaseType"
                className="form-select"
                value={statusFormData.releaseType}
                onChange={handleStatusInputChange}
              >
                <option value="">Select release type</option>
                {(releaseTypes ?? []).map((rt) => (
                  <option
                    key={rt.aa_app_rl_release_type_id}
                    value={String(rt.aa_app_rl_release_type_id)}
                  >
                    {rt.aa_app_rl_release_type_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="statusReleaseDate">Release date</label>
              <input
                id="statusReleaseDate"
                name="releaseDate"
                type="date"
                className="form-input"
                value={statusFormData.releaseDate}
                onChange={handleStatusInputChange}
              />
            </div>
          </div>

          {/* Secondary Owners and Assigned To in same row */}
          <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label htmlFor="statusSecondaryOwners">Secondary Owners</label>
              
              {/* Selected secondary owners display */}
              {statusSelectedSecondaryOwners.length > 0 && (
                <div className="selected-employees">
                  {statusSelectedSecondaryOwners.map((employee) => (
                    <span
                      key={employee.id}
                      className={`employee-tag${
                        statusApplicationEmployeeMismatch.secondaryInvalidIds.has(employee.id)
                          ? ' employee-tag--invalid'
                          : ''
                      }`}
                    >
                      {employee.name}
                      <button
                        type="button"
                        className="remove-employee"
                        onClick={() => removeStatusSecondaryOwner(employee.id)}
                        aria-label={`Remove ${employee.name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Multi-select dropdown with search - editable for all users */}
              <div className="multi-select-dropdown status-secondary-owner-dropdown">
                {/* Search input with dropdown trigger */}
                <div className="search-container">
                    <input
                      type="text"
                      className="form-input search-input"
                      placeholder="Search employees..."
                      value={statusSecondaryOwnerSearchTerm}
                      onChange={handleStatusSecondaryOwnerSearchChange}
                      onFocus={handleStatusSecondaryOwnerSearchFocus}
                    />
                    <button
                      type="button"
                      className="dropdown-trigger"
                      onClick={() => setIsStatusSecondaryOwnerDropdownOpen(!isStatusSecondaryOwnerDropdownOpen)}
                    >
                      <span className={`dropdown-arrow ${isStatusSecondaryOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
                    </button>
                  </div>

                  {/* Dropdown options */}
                  {isStatusSecondaryOwnerDropdownOpen && (
                    <div className="dropdown-menu">
                      {/* Employee options */}
                      {statusFilteredSecondaryOwners.length > 0 ? (
                        statusFilteredSecondaryOwners
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((employee) => {
                            const isSelected = statusSelectedSecondaryOwners.some(
                              (emp) => emp.id === employee.id
                            );
                            return (
                              <div
                                key={employee.id}
                                className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleStatusSecondaryOwnerToggle(employee)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}} // Handled by parent onClick
                                />
                                <span className="option-text">
                                  {highlightSearchTerm(employee.name, statusSecondaryOwnerSearchTerm)}
                                </span>
                              </div>
                            );
                          })
                      ) : (
                        <div className="no-results">
                          No employees found
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {/* Hidden inputs for form submission */}
              {statusFormData.secondaryOwners.map((employeeId, index) => (
                <input
                  key={index}
                  type="hidden"
                  name={`statusSecondaryOwners[${index}]`}
                  value={employeeId}
                />
              ))}
            </div>
            
            {/* Multi-select Assigned To for status form */}
            {renderStatusMultiSelect()}
          </div>

          {/* Status and Completion in same row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">
                Status <span className="required-asterisk">*</span>
              </label>
              <select 
                id="status"
                name="status"
                className="form-select"
                value={statusFormData.status}
                onChange={handleStatusInputChange}
                required
              >
                {statuses.map(status => (
                  <option 
                    key={status.aa_app_rl_task_status_id} 
                    value={status.aa_app_rl_task_status_id}
                  >
                    {status.aa_app_rl_task_status_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group completion-field">
              <label htmlFor="completion">
                Completion: {statusFormData.completion}%
              </label>
              <input 
                id="completion"
                name="completion"
                type="range"
                min="0"
                max="100"
                value={statusFormData.completion}
                onChange={handleStatusInputChange}
                className="form-range"
              />
              <div className="range-labels">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Completion-specific fields (shown when status is "completed") */}
          {isStatusCompleted(statusFormData.status) && (
            <div className="completion-fields-section" style={{ 
              marginTop: '20px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#4CAF50', fontSize: '18px' }}>
                Completion Details
              </h3>
              
              {/* Preset Dropdown */}
              <div className="form-group">
                <label htmlFor="compTypeId">
                  Completion Type <span className="required-asterisk">*</span>
                </label>
                <select 
                  id="compTypeId"
                  name="compTypeId"
                  className="form-select"
                  value={statusFormData.compTypeId}
                  onChange={handleStatusInputChange}
                  required
                >
                  <option value="">Select completion type</option>
                  {completionTypes.map(ct => (
                    <option key={ct.aa_app_rl_comp_type_id} value={String(ct.aa_app_rl_comp_type_id)}>
                      {ct.aa_app_rl_comp_type_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Text Field */}
              <div className="form-group">
                <label htmlFor="compStatusTextDesc">
                  Completion Notes <span className="required-asterisk">*</span>
                </label>
                <textarea 
                  id="compStatusTextDesc"
                  name="compStatusTextDesc"
                  className="form-textarea"
                  placeholder="Provide details about the completion..."
                  value={statusFormData.compStatusTextDesc}
                  onChange={handleStatusInputChange}
                  rows="4"
                  required
                ></textarea>
              </div>

              {/* Attachment Field - Single File */}
              <div className="form-group">
                <label htmlFor="completionFile">
                  Completion File (Images/Files/Screenshots)
                </label>
                <div className="file-attachment-container">
                  <input
                    id="completionFile"
                    type="file"
                    className="file-input"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={handleCompletionFileChange}
                  />
                  <label htmlFor="completionFile" className="file-input-label">
                    <span className="file-input-icon">📎</span>
                    <span className="file-input-text">Choose file to attach</span>
                  </label>
                  <p className="file-input-hint">
                    Supported: Images, PDF, Word, Excel, Text files
                  </p>
                </div>
                
                {/* Display attached completion file */}
                {completionFile && (
                  <div className="attached-files-list" style={{ marginTop: '15px' }}>
                    <div className="attached-file-item">
                      {completionFile.preview ? (
                        <div className="file-preview">
                          <img 
                            src={completionFile.preview} 
                            alt={completionFile.name}
                            className="file-preview-image"
                          />
                        </div>
                      ) : completionFile.isExisting && completionFile.isImage && (completionFilePreviewUrl || completionFile.viewUrl) ? (
                        <div className="file-preview">
                          <img 
                            src={completionFilePreviewUrl || completionFile.viewUrl} 
                            alt={completionFile.name}
                            className="file-preview-image"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                            onError={(e) => {
                              // If image fails to load, hide it and show icon instead
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="file-icon">
                          {(() => {
                            const getFileIcon = (type, fileName) => {
                              if (type) {
                                const typeLower = type.toLowerCase();
                                if (typeLower.includes('image') || typeLower === 'png' || typeLower === 'jpg' || typeLower === 'jpeg') return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (typeLower.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (typeLower.includes('excel') || typeLower.includes('spreadsheet') || typeLower.includes('application/vnd.ms-excel') || typeLower.includes('application/vnd.openxmlformats-officedocument.spreadsheetml')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                                if (typeLower.includes('word') || typeLower.includes('msword') || typeLower.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (typeLower.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                              }
                              if (fileName) {
                                const extension = fileName.split('.').pop().toLowerCase();
                                if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (extension === 'pdf') return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (['doc', 'docx'].includes(extension)) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (['xls', 'xlsx'].includes(extension)) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                              }
                              return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
                            };
                            return getFileIcon(completionFile.type, completionFile.name);
                          })()}
                        </div>
                      )}
                      <div className="file-info">
                        <div className="file-name">{completionFile.name}</div>
                        {completionFile.size && (
                          <div className="file-size">{formatFileSizeHelper(completionFile.size)}</div>
                        )}
                        {completionFile.isExisting && (
                          <div className="file-actions" style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
                            {completionFile.viewUrl && completionFile.isImage && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const response = await axiosInstance.get(completionFile.viewUrl, {
                                      responseType: 'blob',
                                      timeout: 30000
                                    });
                                    
                                    const blob = new Blob([response.data], { 
                                      type: completionFile.type || response.headers['content-type'] || 'image/png'
                                    });
                                    
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    setImageViewer({
                                      isOpen: true,
                                      imageUrl: blobUrl,
                                      fileName: completionFile.name
                                    });
                                  } catch (error) {
                                    console.error('Error viewing completion file:', error);
                                    setDialog({
                                      isOpen: true,
                                      title: "Error",
                                      message: "Failed to view file. Please try downloading it instead.",
                                      type: "error"
                                    });
                                  }
                                }}
                                style={{ 
                                  fontSize: '12px', 
                                  color: '#2196F3', 
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0
                                }}
                              >
                                View
                              </button>
                            )}
                            {completionFile.downloadUrl && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const response = await axiosInstance.get(completionFile.downloadUrl, {
                                      responseType: 'blob',
                                      timeout: 30000
                                    });
                                    
                                    if (!response.data || response.data.size === 0) {
                                      throw new Error('Downloaded file is empty');
                                    }
                                    
                                    const blob = response.data;
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = completionFile.name;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    
                                    // Cleanup
                                    setTimeout(() => {
                                      window.URL.revokeObjectURL(blobUrl);
                                    }, 100);
                                  } catch (error) {
                                    console.error('Error downloading completion file:', error);
                                    setDialog({
                                      isOpen: true,
                                      title: "Error",
                                      message: "Failed to download file. Please try again.",
                                      type: "error"
                                    });
                                  }
                                }}
                                style={{ 
                                  fontSize: '12px', 
                                  color: '#4CAF50', 
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0
                                }}
                              >
                                Download
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {!completionFile.isExisting && (
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={handleRemoveCompletionFile}
                          aria-label={`Remove ${completionFile.name}`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Priority + SLA due date for non-owners (due updates when priority changes) */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">
                Priority <span className="required-asterisk">*</span>
              </label>
              <select 
                id="priority"
                name="priority" 
                className="form-select"
                value={statusFormData.priority}
                onChange={handleStatusInputChange}
                required
              >
                {priorities.map(priority => (
                  <option 
                    key={priority.aa_app_rl_priority_id} 
                    value={priority.aa_app_rl_priority_id}
                  >
                    {priority.aa_app_rl_priority_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="statusDueDate">
                Expected Due Date <span className="required-asterisk">*</span>
              </label>
              <input
                id="statusDueDate"
                name="dueDate"
                type="datetime-local"
                className="form-input"
                value={statusFormData.dueDate}
                readOnly={!canEditTaskOwnerAndDueDate}
                onChange={canEditTaskOwnerAndDueDate ? handleStatusInputChange : undefined}
                step={60}
                title={
                  canEditTaskOwnerAndDueDate
                    ? "Edit the due date, or leave it to update from SLA when you change priority (start time comes from the task)."
                    : "Recalculated from SLA when you change priority (start time comes from the task)."
                }
                required
              />
              <p className="help-text" style={{ marginTop: "6px" }}>
                {canEditTaskOwnerAndDueDate
                  ? "You can edit this date. It also updates from SLA when you change priority; start date/time is fixed on the task."
                  : "Updates when you change priority; start date/time is fixed on the task."}
              </p>
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="comment">
              Today's Update <span className="required-asterisk">*</span>
            </label>
            <textarea 
              id="comment"
              name="comment"
              className="form-textarea"
              placeholder="Add a comment about your progress..."
              value={statusFormData.comment}
              onChange={handleStatusInputChange}
              rows="4"
              required
            ></textarea>
            {task.statusUpdateDesc && (
              <div className="previous-updates">
                <strong>Previous Update:</strong> {task.statusUpdateDesc}
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <label htmlFor="issues">Issues/Blockers</label>
            <textarea 
              id="issues"
              name="issues"
              className="form-textarea"
              placeholder="Document any issues or blockers..."
              value={statusFormData.issues}
              onChange={handleStatusInputChange}
              rows="4"
            ></textarea>
            {task.issues && (
              <div className="previous-issues">
                <strong>Previous Issues:</strong> {task.issues}
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleBack}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
            >
              Update Status
            </button>
          </div>
        </form>
        
        {/* Show status history if available */}
        {renderStatusHistory()}
      </div>
    );
  };

  // Render the owner view with ALL editable fields including task type instead of completion percentage in same row
  const renderOwnerView = () => {
    return (
      <div className="update-form-container owner-view">
        <h3 className="form-title">Update Task Details</h3>
        {taskApplicationEmployeeMismatch.messages.length > 0 && (
          <div className="application-mismatch-banner" role="alert">
            {taskApplicationEmployeeMismatch.messages.map((msg, idx) => (
              <div key={idx}>{msg}</div>
            ))}
          </div>
        )}
        <form onSubmit={handleTaskSubmit} onKeyDown={suppressEnterSubmit} style={{ width: '100%' }}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskStartDate">
                Start date and time <span className="required-asterisk">*</span>
              </label>
              <input
                id="taskStartDate"
                name="startDate"
                type="datetime-local"
                className="form-input"
                value={taskFormData.startDate}
                readOnly
                step={60}
                title="From the task; not editable"
                required
              />
              <p className="help-text" style={{ marginTop: '6px' }}>
                Start comes from the task and cannot be changed here. Due date is recalculated from this time plus SLA when application or priority changes.
              </p>
            </div>
          </div>
          
          {/* Expected Due Date and Priority in same row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dueDate">
                Expected Due Date <span className="required-asterisk">*</span>
              </label>
              <input 
                id="dueDate"
                name="dueDate"
                type="datetime-local"
                className="form-input"
                value={taskFormData.dueDate}
                onChange={handleTaskInputChange}
                step={60}
                title="Editable by the task owner or creator. Also refreshes from SLA when application or priority changes."
                required
              />
              <p className="help-text" style={{ marginTop: '6px' }}>
                Task owners and creators can edit this date. It also updates from SLA when you change application or priority (start time is fixed).
              </p>
            </div>
            
            <div className="form-group">
              <label htmlFor="priority">
                Priority <span className="required-asterisk">*</span>
              </label>
              <select 
                id="priority"
                name="priority" 
                className="form-select"
                value={taskFormData.priority}
                onChange={handleTaskInputChange}
                required
              >
                {priorities.map(priority => (
                  <option 
                    key={priority.aa_app_rl_priority_id} 
                    value={priority.aa_app_rl_priority_id}
                  >
                    {priority.aa_app_rl_priority_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Task Owner and Task Type in same row for task form */}
          <div className="form-row">
            {/* Task Owner dropdown for task form */}
            {renderTaskOwnerDropdown()}
            
            <div className="form-group">
              <label htmlFor="taskType">
                Task Type <span className="required-asterisk">*</span>
              </label>
              <select 
                id="taskType"
                name="taskType"
                className="form-select"
                value={taskFormData.taskType}
                onChange={handleTaskInputChange}
                required
              >
                <option value="">Select a task type</option>
                {taskTypes
                  .slice()
                  .sort((a, b) => a.aa_app_rl_task_type_longname.localeCompare(b.aa_app_rl_task_type_longname))
                  .map((taskType) => (
                    <option 
                      key={taskType.aa_app_rl_task_type_id} 
                      value={taskType.aa_app_rl_task_type_id}
                    >
                      {taskType.aa_app_rl_task_type_longname}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="releaseType">Release type</label>
              <select
                id="releaseType"
                name="releaseType"
                className="form-select"
                value={taskFormData.releaseType}
                onChange={handleTaskInputChange}
              >
                <option value="">Select release type</option>
                {(releaseTypes ?? []).map((rt) => (
                  <option
                    key={rt.aa_app_rl_release_type_id}
                    value={String(rt.aa_app_rl_release_type_id)}
                  >
                    {rt.aa_app_rl_release_type_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="releaseDate">Release date</label>
              <input
                id="releaseDate"
                name="releaseDate"
                type="date"
                className="form-input"
                value={taskFormData.releaseDate}
                onChange={handleTaskInputChange}
              />
            </div>
          </div>

          {/* Secondary Owners and Assigned To in same row for owner */}
          <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label htmlFor="taskSecondaryOwners">Secondary Owners</label>
              
              {/* Selected secondary owners display */}
              {taskSelectedSecondaryOwners.length > 0 && (
                <div className="selected-employees">
                  {taskSelectedSecondaryOwners.map((employee) => (
                    <span
                      key={employee.id}
                      className={`employee-tag${
                        taskApplicationEmployeeMismatch.secondaryInvalidIds.has(employee.id)
                          ? ' employee-tag--invalid'
                          : ''
                      }`}
                    >
                      {employee.name}
                      {isTaskOwner && (
                        <button
                          type="button"
                          className="remove-employee"
                          onClick={() => removeTaskSecondaryOwner(employee.id)}
                          aria-label={`Remove ${employee.name}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Multi-select dropdown with search - only editable for task owners */}
              {isTaskOwner ? (
                <div className="multi-select-dropdown task-secondary-owner-dropdown">
                  {/* Search input with dropdown trigger */}
                  <div className="search-container">
                    <input
                      type="text"
                      className="form-input search-input"
                      placeholder="Search employees..."
                      value={taskSecondaryOwnerSearchTerm}
                      onChange={handleTaskSecondaryOwnerSearchChange}
                      onFocus={handleTaskSecondaryOwnerSearchFocus}
                    />
                    <button
                      type="button"
                      className="dropdown-trigger"
                      onClick={() => setIsTaskSecondaryOwnerDropdownOpen(!isTaskSecondaryOwnerDropdownOpen)}
                    >
                      <span className={`dropdown-arrow ${isTaskSecondaryOwnerDropdownOpen ? 'open' : ''}`}>▼</span>
                    </button>
                  </div>

                  {/* Dropdown options */}
                  {isTaskSecondaryOwnerDropdownOpen && (
                    <div className="dropdown-menu">
                      {/* Employee options */}
                      {taskFilteredSecondaryOwners.length > 0 ? (
                        taskFilteredSecondaryOwners
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((employee) => {
                            const isSelected = taskSelectedSecondaryOwners.some(
                              (emp) => emp.id === employee.id
                            );
                            return (
                              <div
                                key={employee.id}
                                className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleTaskSecondaryOwnerToggle(employee)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}} // Handled by parent onClick
                                />
                                <span className="option-text">
                                  {highlightSearchTerm(employee.name, taskSecondaryOwnerSearchTerm)}
                                </span>
                              </div>
                            );
                          })
                      ) : (
                        <div className="no-results">
                          No employees found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Read-only display for non-task owners */
                <div className="form-input" style={{ 
                  backgroundColor: '#f3f4f6', 
                  cursor: 'not-allowed',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  color: '#6b7280'
                }}>
                  {taskSelectedSecondaryOwners.length > 0 
                    ? taskSelectedSecondaryOwners.map(emp => emp.name).join(', ')
                    : 'No secondary owners assigned'
                  }
                </div>
              )}

              {/* Hidden inputs for form submission */}
              {taskFormData.secondaryOwners.map((employeeId, index) => (
                <input
                  key={index}
                  type="hidden"
                  name={`taskSecondaryOwners[${index}]`}
                  value={employeeId}
                />
              ))}
            </div>
          
            {/* Multi-select Assigned To for task form */}
            {renderTaskMultiSelect()}
          </div>

          {/* Status and Completion in same row for owner */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">
                Status <span className="required-asterisk">*</span>
              </label>
              <select 
                id="status"
                name="status"
                className="form-select"
                value={taskFormData.status}
                onChange={handleTaskInputChange}
                required
              >
                {statuses.map(status => (
                  <option 
                    key={status.aa_app_rl_task_status_id} 
                    value={status.aa_app_rl_task_status_id}
                  >
                    {status.aa_app_rl_task_status_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group completion-field">
              <label htmlFor="completion">
                Completion: {taskFormData.completion}%
              </label>
              <input 
                id="completion"
                name="completion"
                type="range"
                min="0"
                max="100"
                value={taskFormData.completion}
                onChange={handleTaskInputChange}
                className="form-range"
              />
              <div className="range-labels">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Completion-specific fields (shown when status is "completed") */}
          {isStatusCompleted(taskFormData.status) && (
            <div className="completion-fields-section" style={{ 
              marginTop: '20px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#4CAF50', fontSize: '18px' }}>
                Completion Details
              </h3>
              
              {/* Preset Dropdown */}
              <div className="form-group">
                <label htmlFor="compTypeId">
                  Completion Type <span className="required-asterisk">*</span>
                </label>
                <select 
                  id="compTypeId"
                  name="compTypeId"
                  className="form-select"
                  value={taskFormData.compTypeId}
                  onChange={handleTaskInputChange}
                  required
                >
                  <option value="">Select completion type</option>
                  {completionTypes.map(ct => (
                    <option key={ct.aa_app_rl_comp_type_id} value={String(ct.aa_app_rl_comp_type_id)}>
                      {ct.aa_app_rl_comp_type_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Text Field */}
              <div className="form-group">
                <label htmlFor="compStatusTextDesc">
                  Completion Notes <span className="required-asterisk">*</span>
                </label>
                <textarea 
                  id="compStatusTextDesc"
                  name="compStatusTextDesc"
                  className="form-textarea"
                  placeholder="Provide details about the completion..."
                  value={taskFormData.compStatusTextDesc}
                  onChange={handleTaskInputChange}
                  rows="4"
                  required
                ></textarea>
              </div>

              {/* Attachment Field - Single File */}
              <div className="form-group">
                <label htmlFor="completionFile">
                  Completion File (Images/Files/Screenshots)
                </label>
                <div className="file-attachment-container">
                  <input
                    id="completionFile"
                    type="file"
                    className="file-input"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={handleCompletionFileChange}
                  />
                  <label htmlFor="completionFile" className="file-input-label">
                    <span className="file-input-icon">📎</span>
                    <span className="file-input-text">Choose file to attach</span>
                  </label>
                  <p className="file-input-hint">
                    Supported: Images, PDF, Word, Excel, Text files
                  </p>
                </div>
                
                {/* Display attached completion file */}
                {completionFile && (
                  <div className="attached-files-list" style={{ marginTop: '15px' }}>
                    <div className="attached-file-item">
                      {completionFile.preview ? (
                        <div className="file-preview">
                          <img 
                            src={completionFile.preview} 
                            alt={completionFile.name}
                            className="file-preview-image"
                          />
                        </div>
                      ) : completionFile.isExisting && completionFile.isImage && (completionFilePreviewUrl || completionFile.viewUrl) ? (
                        <div className="file-preview">
                          <img 
                            src={completionFilePreviewUrl || completionFile.viewUrl} 
                            alt={completionFile.name}
                            className="file-preview-image"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                            onError={(e) => {
                              // If image fails to load, hide it and show icon instead
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="file-icon">
                          {(() => {
                            const getFileIcon = (type, fileName) => {
                              if (type) {
                                const typeLower = type.toLowerCase();
                                if (typeLower.includes('image') || typeLower === 'png' || typeLower === 'jpg' || typeLower === 'jpeg') return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (typeLower.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (typeLower.includes('excel') || typeLower.includes('spreadsheet') || typeLower.includes('application/vnd.ms-excel') || typeLower.includes('application/vnd.openxmlformats-officedocument.spreadsheetml')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                                if (typeLower.includes('word') || typeLower.includes('msword') || typeLower.includes('application/vnd.openxmlformats-officedocument.wordprocessingml')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (typeLower.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
                              }
                              if (fileName) {
                                const extension = fileName.split('.').pop().toLowerCase();
                                if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
                                if (extension === 'pdf') return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
                                if (['doc', 'docx'].includes(extension)) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
                                if (['xls', 'xlsx'].includes(extension)) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
                              }
                              return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
                            };
                            return getFileIcon(completionFile.type, completionFile.name);
                          })()}
                        </div>
                      )}
                      <div className="file-info">
                        <div className="file-name">{completionFile.name}</div>
                        {completionFile.size && (
                          <div className="file-size">{formatFileSizeHelper(completionFile.size)}</div>
                        )}
                        {completionFile.isExisting && (
                          <div className="file-actions" style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
                            {completionFile.viewUrl && completionFile.isImage && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const response = await axiosInstance.get(completionFile.viewUrl, {
                                      responseType: 'blob',
                                      timeout: 30000
                                    });
                                    
                                    const blob = new Blob([response.data], { 
                                      type: completionFile.type || response.headers['content-type'] || 'image/png'
                                    });
                                    
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    setImageViewer({
                                      isOpen: true,
                                      imageUrl: blobUrl,
                                      fileName: completionFile.name
                                    });
                                  } catch (error) {
                                    console.error('Error viewing completion file:', error);
                                    setDialog({
                                      isOpen: true,
                                      title: "Error",
                                      message: "Failed to view file. Please try downloading it instead.",
                                      type: "error"
                                    });
                                  }
                                }}
                                style={{ 
                                  fontSize: '12px', 
                                  color: '#2196F3', 
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0
                                }}
                              >
                                View
                              </button>
                            )}
                            {completionFile.downloadUrl && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const response = await axiosInstance.get(completionFile.downloadUrl, {
                                      responseType: 'blob',
                                      timeout: 30000
                                    });
                                    
                                    if (!response.data || response.data.size === 0) {
                                      throw new Error('Downloaded file is empty');
                                    }
                                    
                                    const blob = response.data;
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = completionFile.name;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    
                                    // Cleanup
                                    setTimeout(() => {
                                      window.URL.revokeObjectURL(blobUrl);
                                    }, 100);
                                  } catch (error) {
                                    console.error('Error downloading completion file:', error);
                                    setDialog({
                                      isOpen: true,
                                      title: "Error",
                                      message: "Failed to download file. Please try again.",
                                      type: "error"
                                    });
                                  }
                                }}
                                style={{ 
                                  fontSize: '12px', 
                                  color: '#4CAF50', 
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0
                                }}
                              >
                                Download
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {!completionFile.isExisting && (
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={handleRemoveCompletionFile}
                          aria-label={`Remove ${completionFile.name}`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Added status update fields for owner */}
          <div className="form-group full-width">
            <label htmlFor="comment">Status Update</label>
            <textarea 
              id="comment"
              name="comment"
              className="form-textarea"
              placeholder="Add a comment about the current status..."
              value={taskFormData.comment}
              onChange={handleTaskInputChange}
              rows="4"
            ></textarea>
            {task.statusUpdateDesc && taskFormData.comment !== task.statusUpdateDesc && (
              <div className="previous-updates">
                <strong>Previous Update:</strong> {task.statusUpdateDesc}
              </div>
            )}
          </div>

          <div className="form-group full-width">
            <label htmlFor="issues">Issues/Blockers</label>
            <textarea 
              id="issues"
              name="issues"
              className="form-textarea"
              placeholder="Document any issues or blockers..."
              value={taskFormData.issues}
              onChange={handleTaskInputChange}
              rows="4"
            ></textarea>
            {task.issues && taskFormData.issues !== task.issues && (
              <div className="previous-issues">
                <strong>Previous Issues:</strong> {task.issues}
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleBack}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update Task Details"}
            </button>
          </div>
        </form>
        
        {/* Show status history if available */}
        {renderStatusHistory()}
      </div>
    );
  };
    
    // Get display classes for status
    const getStatusClassName = () => {
      return task.statusName.toLowerCase().replace(/\s+/g, '-');
    };
    
    return (
      <div className="update-task-screen">
      <div className="screen-header">
        <h1 className="screen-title">
          {isTaskOwner || isFunctionalLead ? "Manage Task" : "Update Task Status"}
        </h1>
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back
        </button>
      </div>
      
      <div className="task-details-container">
        <div className="task-details">
          {canEditTitleAndDescription ? (
            <div className="task-title-edit-block" style={{ width: "100%", marginBottom: "1rem" }}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label htmlFor="headerTaskTitle">Task title</label>
                <input
                  id="headerTaskTitle"
                  name="title"
                  type="text"
                  className="form-input"
                  value={
                    isTaskOwner
                      ? taskFormData.title ?? ""
                      : statusFormData.title ?? ""
                  }
                  onChange={handleEditableTitleDescriptionChange}
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="headerTaskDescription">Task description</label>
                <textarea
                  id="headerTaskDescription"
                  name="description"
                  className="form-textarea"
                  rows={4}
                  value={
                    isTaskOwner
                      ? taskFormData.description ?? ""
                      : statusFormData.description ?? ""
                  }
                  onChange={handleEditableTitleDescriptionChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          ) : (
            <>
              <h2 className="task-title">{task.title}</h2>
              <p className="task-description">{task.description}</p>
            </>
          )}
          
          <div className="task-meta-section">
            <div className="meta-item">
              <span className="meta-label">Task ID:</span>
              <span className="meta-value">{task.id}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Application:</span>
              <span className="meta-value">{task.application}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Created By:</span>
              <span className="meta-value">{task.createdByName}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Creation Date:</span>
              <span className="meta-value">{task.creationDate}</span>
            </div>
            {task.taskOwnerName && (
              <div className="meta-item">
                <span className="meta-label">Task Owner:</span>
                <span className="meta-value">{task.taskOwnerName}</span>
              </div>
            )}
            {task.taskTypeName && (
              <div className="meta-item">
                <span className="meta-label">Task Type:</span>
                <span className="meta-value">{task.taskTypeName}</span>
              </div>
            )}
            <div className="meta-item">
              <span className="meta-label">Secondary Owners:</span>
              <span className="meta-value">
                {taskSelectedSecondaryOwners.length > 0 
                  ? taskSelectedSecondaryOwners.map(emp => emp.name).join(', ')
                  : 'None'
                }
              </span>
            </div>
            {!isTaskOwner && (
              <div className="meta-item">
                <span className="meta-label">Current Status:</span>
                <span className="meta-value">{task.statusName} - {task.completion}%</span>
              </div>
            )}
            <div className="meta-item">
              <span className="meta-label">Currently Assigned To:</span>
              <span className="meta-value">
                {taskSelectedEmployees.length > 0 
                  ? taskSelectedEmployees.map(emp => emp.name).join(', ')
                  : 'No one assigned'
                }
              </span>
            </div>
          </div>
          
          {/* Display attachments if any - moved to end */}
          {task.attachments && Array.isArray(task.attachments) && task.attachments.length > 0 ? (
            <div className="task-attachments-section">
              <h3 className="attachments-title">Attachments ({task.attachments.length})</h3>
              <div className="task-attachments-list">
                {task.attachments.map((attachment, index) => {
                  // Handle backend response format with aa_app_task_file_* fields
                  // Use helper function to extract attachment ID
                  const attachmentId = getAttachmentId(attachment);
                  
                  const attachmentName = attachment.aa_app_task_file_original_name || attachment.name || attachment.file_name || attachment.attachment_name || attachment.fileName || attachment.original_name || attachment.originalName || `Attachment ${index + 1}`;
                  
                  // Debug: Log attachment data for all attachments to find the ID
                  console.log(`📎 Attachment ${index + 1} data:`, {
                    index,
                    attachmentId,
                    attachmentIdType: typeof attachmentId,
                    attachmentName,
                    'aa_app_task_file_attachment_id': attachment.aa_app_task_file_attachment_id,
                    'id': attachment.id,
                    'attachment_id': attachment.attachment_id,
                    'file_attachment_id': attachment.file_attachment_id,
                    allKeys: Object.keys(attachment),
                    rawAttachment: attachment
                  });
                  const savedFileName = attachment.aa_app_task_file_saved_name || attachment.saved_name || attachment.fileName;
                  const filePath = attachment.aa_app_task_file_path || attachment.file_path || attachment.path;
                  const uploadDate = attachment.aa_app_task_file_upload_date || attachment.upload_date || attachment.created_at;
                  const base64Content = attachment.aa_app_task_file_content_base64 || attachment.base64_content || attachment.content_base64;
                  const fileSize = attachment.aa_app_task_file_size || attachment.file_size || attachment.size;
                  
                  // Helper function to convert base64 to blob
                  const base64ToBlob = (base64String, mimeType = 'application/octet-stream') => {
                    try {
                      if (!base64String || base64String.trim() === '') {
                        console.error('Base64 string is empty');
                        return null;
                      }
                      
                      // Remove any whitespace
                      const cleanBase64 = base64String.trim();
                      
                      // Handle data URL format (data:mime/type;base64,actualBase64String)
                      let actualBase64 = cleanBase64;
                      if (cleanBase64.includes(',')) {
                        const parts = cleanBase64.split(',');
                        actualBase64 = parts[1];
                        // Extract MIME type from data URL if available
                        const mimeMatch = parts[0].match(/data:([^;]+)/);
                        if (mimeMatch && mimeMatch[1]) {
                          mimeType = mimeMatch[1];
                        }
                      }
                      
                      console.log('📎 Converting base64 to blob, length:', actualBase64.length, 'MIME:', mimeType);
                      
                      const byteCharacters = atob(actualBase64);
                      const byteNumbers = new Array(byteCharacters.length);
                      for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                      }
                      const byteArray = new Uint8Array(byteNumbers);
                      const blob = new Blob([byteArray], { type: mimeType });
                      
                      console.log('📎 Blob created successfully, size:', blob.size, 'bytes');
                      return blob;
                    } catch (error) {
                      console.error('📎 Error converting base64 to blob:', error);
                      console.error('📎 Base64 string preview:', base64String.substring(0, 200));
                      return null;
                    }
                  };
                  
                  // Helper function to get MIME type from filename
                  const getMimeTypeFromFileName = (fileName) => {
                    if (!fileName) return 'application/octet-stream';
                    const extension = fileName.split('.').pop().toLowerCase();
                    const mimeTypes = {
                      // Images
                      'jpg': 'image/jpeg',
                      'jpeg': 'image/jpeg',
                      'png': 'image/png',
                      'gif': 'image/gif',
                      'bmp': 'image/bmp',
                      'webp': 'image/webp',
                      'svg': 'image/svg+xml',
                      'ico': 'image/x-icon',
                      'tiff': 'image/tiff',
                      'tif': 'image/tiff',
                      'heic': 'image/heic',
                      'heif': 'image/heif',
                      // Documents
                      'pdf': 'application/pdf',
                      'doc': 'application/msword',
                      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'rtf': 'application/rtf',
                      'odt': 'application/vnd.oasis.opendocument.text',
                      // Spreadsheets
                      'xls': 'application/vnd.ms-excel',
                      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'ods': 'application/vnd.oasis.opendocument.spreadsheet',
                      'csv': 'text/csv',
                      // Presentations
                      'ppt': 'application/vnd.ms-powerpoint',
                      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                      'odp': 'application/vnd.oasis.opendocument.presentation',
                      // Text files
                      'txt': 'text/plain',
                      'html': 'text/html',
                      'htm': 'text/html',
                      'css': 'text/css',
                      'js': 'text/javascript',
                      'json': 'application/json',
                      'xml': 'application/xml',
                      // Archives
                      'zip': 'application/zip',
                      'rar': 'application/x-rar-compressed',
                      '7z': 'application/x-7z-compressed',
                      'tar': 'application/x-tar',
                      'gz': 'application/gzip',
                      // Video
                      'mp4': 'video/mp4',
                      'avi': 'video/x-msvideo',
                      'mov': 'video/quicktime',
                      'wmv': 'video/x-ms-wmv',
                      'webm': 'video/webm',
                      'mkv': 'video/x-matroska',
                      // Audio
                      'mp3': 'audio/mpeg',
                      'wav': 'audio/wav',
                      'ogg': 'audio/ogg',
                      'm4a': 'audio/mp4',
                      'flac': 'audio/flac'
                    };
                    return mimeTypes[extension] || 'application/octet-stream';
                  };
                  
                  // Check if we have base64 content (new format) - check for non-empty string
                  const hasBase64Content = base64Content && typeof base64Content === 'string' && base64Content.trim().length > 0;
                  
                  // Always try to create download URL as fallback (even if base64 exists, URL can be used as backup)
                  let attachmentUrl = null;
                  
                  // Helper function to build URL with optional task ID
                  const buildDownloadUrl = (paramName, paramValue) => {
                    if (!paramValue) return null;
                    let url = `${API_BASE_URL}/downloadTaskAttachment?${paramName}=${encodeURIComponent(paramValue)}`;
                    if (taskId) {
                      url += `&aa_app_rl_resource_task_uid=${taskId}`;
                    }
                    return url;
                  };
                  
                  // First priority: Use savedFileName (most reliable for file access)
                  if (savedFileName) {
                    attachmentUrl = buildDownloadUrl('aa_app_task_file_saved_name', savedFileName);
                  } 
                  // Second priority: Use attachment ID if it's not 0, null, or undefined
                  else if (attachmentId !== null && attachmentId !== undefined && attachmentId !== 0) {
                    attachmentUrl = buildDownloadUrl('aa_app_task_file_attachment_id', attachmentId);
                  } 
                  // Third priority: Extract filename from file path
                  else if (filePath) {
                    const fileNameFromPath = filePath.split('/').pop() || filePath.split('\\').pop();
                    attachmentUrl = buildDownloadUrl('filename', fileNameFromPath);
                  }
                  
                  // Try alternative field names for URL
                  if (!attachmentUrl) {
                    attachmentUrl = attachment.url || attachment.file_url || attachment.attachment_url || attachment.fileUrl || attachment.download_url || attachment.downloadUrl;
                  }
                  
                  // If we still don't have a URL but have attachmentName, try using it as a last resort
                  if (!attachmentUrl && attachmentName && attachmentName !== `Attachment ${index + 1}`) {
                    attachmentUrl = buildDownloadUrl('filename', attachmentName);
                  }
                  
                  // If we have taskId but still no URL, try with just taskId and attachmentName
                  if (!attachmentUrl && taskId && attachmentName && attachmentName !== `Attachment ${index + 1}`) {
                    attachmentUrl = `${API_BASE_URL}/downloadTaskAttachment?aa_app_rl_resource_task_uid=${taskId}&filename=${encodeURIComponent(attachmentName)}`;
                  }
                  
                  // Determine file type from file extension
                  const getFileTypeFromName = (fileName) => {
                    if (!fileName) return '';
                    const extension = fileName.split('.').pop().toLowerCase();
                    const typeMap = {
                      // Images
                      'jpg': 'image/jpeg',
                      'jpeg': 'image/jpeg',
                      'png': 'image/png',
                      'gif': 'image/gif',
                      'bmp': 'image/bmp',
                      'webp': 'image/webp',
                      'svg': 'image/svg+xml',
                      'ico': 'image/x-icon',
                      'tiff': 'image/tiff',
                      'tif': 'image/tiff',
                      // Documents
                      'pdf': 'application/pdf',
                      'doc': 'application/msword',
                      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'rtf': 'application/rtf',
                      // Spreadsheets
                      'xls': 'application/vnd.ms-excel',
                      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'csv': 'text/csv',
                      // Presentations
                      'ppt': 'application/vnd.ms-powerpoint',
                      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                      // Text files
                      'txt': 'text/plain',
                      'html': 'text/html',
                      'htm': 'text/html',
                      'json': 'application/json',
                      'xml': 'application/xml'
                    };
                    return typeMap[extension] || '';
                  };
                  
                  const attachmentType = attachment.type || attachment.file_type || attachment.mime_type || attachment.content_type || attachment.contentType || getFileTypeFromName(attachmentName);
                  const attachmentSize = fileSize || attachment.size || attachment.file_size || attachment.fileSize || '';
                  
                  // Debug: Log all available fields in attachment object
                  console.log(`📎 Attachment ${index + 1} - All fields:`, Object.keys(attachment));
                  console.log(`📎 Attachment ${index + 1}:`, {
                    id: attachmentId,
                    url: attachmentUrl,
                    name: attachmentName,
                    savedName: savedFileName,
                    path: filePath,
                    type: attachmentType,
                    size: attachmentSize,
                    uploadDate: uploadDate,
                    hasBase64: hasBase64Content,
                    base64Length: base64Content ? base64Content.length : 0,
                    raw: attachment
                  });
                  
                  // Final check: if we still don't have URL or base64, log warning
                  if (!hasBase64Content && !attachmentUrl) {
                    console.warn(`📎 Warning: No download method available for attachment ${index + 1}`, {
                      attachmentId,
                      savedFileName,
                      filePath,
                      attachmentName,
                      availableFields: Object.keys(attachment)
                    });
                  }
                  
                  // Helper function to open blob in new browser tab
                  const openBlobInNewTab = (blob, fileName) => {
                    try {
                      if (!blob) {
                        throw new Error('Blob is null or undefined');
                      }
                      
                      console.log('📎 Opening blob in browser - Size:', blob.size, 'Type:', blob.type, 'File:', fileName);
                      
                      const blobUrl = window.URL.createObjectURL(blob);
                      console.log('📎 Created blob URL:', blobUrl);
                      
                      // Determine if file can be viewed in browser
                      const fileExtension = fileName ? fileName.split('.').pop().toLowerCase() : '';
                      const viewableInBrowser = [
                        'pdf', 'txt', 'html', 'htm', 'xml', 'json', 'css', 'js',
                        'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp',
                        'mp4', 'webm', 'ogg',
                        'mp3', 'wav', 'm4a'
                      ].includes(fileExtension);
                      
                      console.log('📎 File extension:', fileExtension, 'Viewable in browser:', viewableInBrowser);
                      
                      // Try to open in new tab
                      let newWindow = null;
                      try {
                        newWindow = window.open(blobUrl, '_blank');
                      } catch (openError) {
                        console.warn('📎 window.open failed, trying alternative:', openError);
                      }
                      
                      // Check if popup was blocked or failed
                      if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
                        console.warn('📎 Popup blocked or failed, trying alternative method');
                        
                        // Alternative method 1: Create a temporary link and click it
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        
                        // For files that might not open in browser, add download attribute as fallback
                        if (!viewableInBrowser) {
                          a.download = fileName;
                          console.log('📎 File may not be viewable in browser, adding download attribute');
                        }
                        
                        document.body.appendChild(a);
                        a.click();
                        
                        // Remove link after a short delay
                        setTimeout(() => {
                          document.body.removeChild(a);
                        }, 100);
                        
                        // Cleanup blob URL after a delay (give browser time to load)
                        setTimeout(() => {
                          try {
                            window.URL.revokeObjectURL(blobUrl);
                            console.log('📎 Blob URL revoked (alternative method)');
                          } catch (e) {
                            console.warn('📎 Error revoking blob URL:', e);
                          }
                        }, 2000);
                      } else {
                        // Popup opened successfully
                        console.log('📎 File opened in new tab successfully');
                        
                        // For Office files and other non-viewable types, the browser might download instead
                        // This is expected behavior - the file will download if it can't be displayed
                        setTimeout(() => {
                          try {
                            if (newWindow && !newWindow.closed) {
                              // Window is still open, likely viewing the file
                              console.log('📎 File is being viewed in browser tab');
                            } else {
                              console.log('📎 Browser tab may have closed or file was downloaded');
                            }
                          } catch (e) {
                            // Cross-origin restrictions might prevent checking
                            console.log('📎 Cannot verify tab status (cross-origin restriction)');
                          }
                        }, 1000);
                        
                        // Cleanup blob URL when tab closes (best effort)
                        try {
                          newWindow.addEventListener('beforeunload', () => {
                            try {
                              window.URL.revokeObjectURL(blobUrl);
                              console.log('📎 Blob URL revoked (tab closed)');
                            } catch (e) {
                              console.warn('📎 Error revoking blob URL on tab close:', e);
                            }
                          });
                        } catch (e) {
                          console.warn('📎 Could not add beforeunload listener:', e);
                          // Fallback: cleanup after a longer delay
                          setTimeout(() => {
                            try {
                              window.URL.revokeObjectURL(blobUrl);
                            } catch (revokeError) {
                              console.warn('📎 Error revoking blob URL:', revokeError);
                            }
                          }, 10000);
                        }
                      }
                      
                      console.log('📎 File should now be open in browser');
                    } catch (error) {
                      console.error('📎 Error opening blob in new tab:', error);
                      console.error('📎 Error stack:', error.stack);
                      throw error;
                    }
                  };
                  
                  // Format file size
                  const formatFileSize = (bytes) => {
                    if (!bytes || bytes === 0) return '';
                    const k = 1024;
                    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                    const i = Math.floor(Math.log(bytes) / Math.log(k));
                    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
                  };
                  
                  // Check if file is an image/screenshot
                  const isImageFile = (type, fileName) => {
                    // Check MIME type
                    if (type && type.startsWith('image/')) {
                      return true;
                    }
                    // Check file extension as fallback
                    if (fileName) {
                      const extension = fileName.split('.').pop().toLowerCase();
                      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif'];
                      return imageExtensions.includes(extension);
                    }
                    return false;
                  };
                  
                  const isImage = isImageFile(attachmentType, attachmentName);
                  
                  return (
                    <div key={index} className="task-attachment-item">
                      <div className="attachment-icon">
                        {getFileIcon(attachmentType, attachmentName)}
                      </div>
                      <div className="attachment-info">
                        <div className="attachment-name">{attachmentName}</div>
                        {attachmentSize && (
                          <div className="attachment-size">{formatFileSize(attachmentSize)}</div>
                        )}
                      </div>
                      <div className="attachment-actions">
                      {(hasBase64Content || attachmentUrl) ? (
                        <div style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
                          {/* View button - only for images/screenshots */}
                          {isImage && (hasBase64Content || attachmentUrl) && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                let blob = null;
                                
                                // If we have base64 content, use it directly
                                if (hasBase64Content && base64Content) {
                                  // Clean base64 string (remove data URL prefix if present)
                                  let cleanBase64 = base64Content;
                                  if (base64Content.includes(',')) {
                                    cleanBase64 = base64Content.split(',')[1];
                                  }
                                  
                                  const mimeType = getMimeTypeFromFileName(attachmentName);
                                  blob = base64ToBlob(cleanBase64, mimeType);
                                } 
                                // Otherwise, fetch from URL (fallback)
                                else if (attachmentUrl) {
                                  const response = await axiosInstance.get(attachmentUrl, {
                                    responseType: 'blob',
                                    timeout: 30000
                                  });
                                  
                                  blob = new Blob([response.data], { 
                                    type: response.headers['content-type'] || getMimeTypeFromFileName(attachmentName) || 'image/png'
                                  });
                                }
                                
                                if (blob) {
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  setImageViewer({
                                    isOpen: true,
                                    imageUrl: blobUrl,
                                    fileName: attachmentName
                                  });
                                } else {
                                  throw new Error('Failed to create blob for viewing');
                                }
                              } catch (error) {
                                console.error('Error viewing attachment:', error);
                                setDialog({
                                  isOpen: true,
                                  title: "Error",
                                  message: "Failed to view file. Please try downloading it instead.",
                                  type: "error"
                                });
                              }
                            }}
                            style={{ 
                              fontSize: '12px', 
                              color: '#2196F3', 
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0
                            }}
                          >
                            View
                          </button>
                          )}
                          
                          {/* Download button - shown for all file types */}
                          {(hasBase64Content || attachmentUrl) && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                let blob = null;
                                
                                // If we have base64 content, use it directly
                                if (hasBase64Content && base64Content) {
                                  // Clean base64 string (remove data URL prefix if present)
                                  let cleanBase64 = base64Content;
                                  if (base64Content.includes(',')) {
                                    cleanBase64 = base64Content.split(',')[1];
                                  }
                                  
                                  const mimeType = getMimeTypeFromFileName(attachmentName);
                                  blob = base64ToBlob(cleanBase64, mimeType);
                                } 
                                // Otherwise, fetch from URL
                                else if (attachmentUrl) {
                                  const response = await axiosInstance.get(attachmentUrl, {
                                    responseType: 'blob',
                                    timeout: 30000
                                  });
                                  
                                  if (!response.data || response.data.size === 0) {
                                    throw new Error('Downloaded file is empty');
                                  }
                                  
                                  blob = response.data;
                                } else {
                                  throw new Error('No file content available to download');
                                }
                                
                                if (blob) {
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = blobUrl;
                                  link.download = attachmentName;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  
                                  // Cleanup
                                  setTimeout(() => {
                                    window.URL.revokeObjectURL(blobUrl);
                                  }, 100);
                                } else {
                                  throw new Error('Failed to create blob for download');
                                }
                              } catch (error) {
                                console.error('Error downloading attachment:', error);
                                setDialog({
                                  isOpen: true,
                                  title: "Error",
                                  message: "Failed to download file. Please try again.",
                                  type: "error"
                                });
                              }
                            }}
                            style={{ 
                              fontSize: '12px', 
                              color: '#4CAF50', 
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0
                            }}
                          >
                            Download
                          </button>
                          )}
                        </div>
                      ) : (
                        <>
                          <span className="attachment-download-btn" style={{ opacity: 0.5, cursor: 'not-allowed' }} title="Download URL not available">
                            <i className="fas fa-download"></i>
                          </span>
                        </>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* File upload option - available to everyone */}
              <div className="add-attachment-section" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                  <div className="file-attachment-container" style={{ minHeight: '80px', padding: '15px' }}>
                    <input
                      id="fileAttachmentInSection"
                      type="file"
                      className="file-input"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="fileAttachmentInSection" className="file-input-label" style={{ padding: '10px' }}>
                      <span className="file-input-icon">📎</span>
                      <span className="file-input-text">Choose files to attach</span>
                    </label>
                    <p className="file-input-hint" style={{ marginTop: '8px', fontSize: '12px' }}>
                      Supported: Images, PDF, Word, Excel, Text files
                    </p>
                  </div>
                  
                  {/* Display newly attached files (before submission) */}
                  {attachedFiles.length > 0 && (
                    <div className="attached-files-list" style={{ marginTop: '15px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#666' }}>New files to be attached:</div>
                      {attachedFiles.map((fileObj) => (
                        <div key={fileObj.id} className="attached-file-item">
                          {fileObj.preview ? (
                            <div className="file-preview">
                              <img 
                                src={fileObj.preview} 
                                alt={fileObj.name}
                                className="file-preview-image"
                              />
                            </div>
                          ) : (
                            <div className="file-icon">
                              {getFileIcon(fileObj.type, fileObj.name)}
                            </div>
                          )}
                          <div className="file-info">
                            <div className="file-name">{fileObj.name}</div>
                            <div className="file-size">{formatFileSizeHelper(fileObj.size)}</div>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => handleRemoveFile(fileObj.id)}
                            aria-label={`Remove ${fileObj.name}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          ) : (
            // Show file upload option even when there are no existing attachments - available to everyone
            <div className="task-attachments-section">
                <h3 className="attachments-title">Attachments (0)</h3>
                <div className="add-attachment-section">
                  <div className="file-attachment-container" style={{ minHeight: '80px', padding: '15px' }}>
                    <input
                      id="fileAttachmentInSectionEmpty"
                      type="file"
                      className="file-input"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="fileAttachmentInSectionEmpty" className="file-input-label" style={{ padding: '10px' }}>
                      <span className="file-input-icon">📎</span>
                      <span className="file-input-text">Choose files to attach</span>
                    </label>
                    <p className="file-input-hint" style={{ marginTop: '8px', fontSize: '12px' }}>
                      Supported: Images, PDF, Word, Excel, Text files
                    </p>
                  </div>
                  
                  {/* Display newly attached files (before submission) */}
                  {attachedFiles.length > 0 && (
                    <div className="attached-files-list" style={{ marginTop: '15px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#666' }}>New files to be attached:</div>
                      {attachedFiles.map((fileObj) => (
                        <div key={fileObj.id} className="attached-file-item">
                          {fileObj.preview ? (
                            <div className="file-preview">
                              <img 
                                src={fileObj.preview} 
                                alt={fileObj.name}
                                className="file-preview-image"
                              />
                            </div>
                          ) : (
                            <div className="file-icon">
                              {getFileIcon(fileObj.type, fileObj.name)}
                            </div>
                          )}
                          <div className="file-info">
                            <div className="file-name">{fileObj.name}</div>
                            <div className="file-size">{formatFileSizeHelper(fileObj.size)}</div>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => handleRemoveFile(fileObj.id)}
                            aria-label={`Remove ${fileObj.name}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          )}
        </div>
        
        {/* Render different forms based on task ownership */}
        {isTaskOwner ? renderOwnerView() : renderStatusUpdateView()}
      </div>

      {/* Mandatory Fields Legend */}
      <div className="mandatory-legend">
        <span className="required-asterisk">*</span>
        <span className="legend-text">Indicates mandatory field</span>
      </div>

      {/* Loading Spinner Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-spinner-container">
            <div className="circular-spinner"></div>
            <p className="loading-text">Updating Task...</p>
          </div>
        </div>
      )}

      {/* Centered Dialog */}
      <CenteredDialog
        isOpen={dialog.isOpen}
        onClose={dialog.onClose || (() => setDialog({ ...dialog, isOpen: false }))}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        isConfirm={dialog.isConfirm || false}
        onConfirm={dialog.onConfirm || null}
        confirmText={dialog.confirmText || "Yes"}
        cancelText={dialog.cancelText || "No"}
      />

      {/* Image Viewer Modal */}
      {imageViewer.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
            if (imageViewer.imageUrl) {
              window.URL.revokeObjectURL(imageViewer.imageUrl);
            }
            setImageViewer({ isOpen: false, imageUrl: null, fileName: null });
          }}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                if (imageViewer.imageUrl) {
                  window.URL.revokeObjectURL(imageViewer.imageUrl);
                }
                setImageViewer({ isOpen: false, imageUrl: null, fileName: null });
              }}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#333',
                fontWeight: 'bold',
                zIndex: 10001
              }}
              title="Close"
            >
              ×
            </button>
            {imageViewer.imageUrl && (
              <img
                src={imageViewer.imageUrl}
                alt={imageViewer.fileName || 'Image'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                }}
              />
            )}
            {imageViewer.fileName && (
              <div
                style={{
                  marginTop: '10px',
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '100%',
                  wordBreak: 'break-word'
                }}
              >
                {imageViewer.fileName}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateTaskScreen;